
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Sun, Mic, MessageSquare, 
  MoveRight, MoveLeft, GripVertical, X,
  User as UserIcon, ChevronDown, Check, PanelLeftClose, PanelLeftOpen,
  Square, Library, LayoutGrid, Settings, Moon, SunMoon, Link, Unlink,
  FileEdit, Crown, Layout, HelpCircle, Pin, PinOff, UploadCloud, CheckCircle, RefreshCw, Users, Save,
  ToggleLeft, ToggleRight, Maximize2, Minimize2, LogOut, Plus, ExternalLink, Download,
  ShieldAlert, Smartphone, Palette, Zap, Database, Wrench, Brain, Sparkles, Cast, Activity
} from 'lucide-react';

const TAB_ID = Math.random().toString(36).substring(2, 11);
import { motion, AnimatePresence } from 'motion/react';
import { ViewState, ModularLayoutType, SidebarPosition, UserProfile, CollaborationStatus, UIState, ReportTemplate } from '../types';
import { User as FirebaseUser } from 'firebase/auth';
import { subscribeToUserProfile, saveUserProfile, saveUIState, subscribeToActiveChats, subscribeToWorkGroups, subscribeToTextStream, subscribeToRegionalRestrictions } from '../services/db';
import { AudioRecorder, blobToBase64, compressAudio } from '../services/audioUtils';
import { GeminiLiveManager } from '../services/liveTranscription';
import { selectAgentTemplateAndMode, transcribeAudio } from '../services/geminiService';
import { generateExtensionZip } from '../services/extensionService';
import { getRegionalAccess } from '../utils/regionalAccess';
import BottomNav from './BottomNav';
import Modal from './Modal';

import TermsModal from './TermsModal';
import { TERMS_VERSION } from '../constants';

interface ModuleSlot {
  id: number;
  view: ViewState | null;
}

type AppProfile = 'Basico' | 'Medio' | 'Avanzado' | 'Libre';
type AppTheme = 'day' | 'mixed' | 'dark';

const MODULE_META: Record<string, { label: string; icon: any }> = {
  [ViewState.REPORTS]: { label: 'Biblioteca', icon: Library }, 
  [ViewState.DICTAPHONE]: { label: 'Transcripción IA', icon: Mic },
  [ViewState.MODULAR_1]: { label: 'MODULAR-1', icon: Sun },
  [ViewState.CHAT_IA]: { label: 'Búsqueda IA', icon: MessageSquare },
  [ViewState.COMMUNITY]: { label: 'Comunidad', icon: Users },
  [ViewState.PROFILE]: { label: 'Perfil del Radiólogo', icon: UserIcon },
  [ViewState.ADMIN_PANEL]: { label: 'Gestión Master', icon: ShieldAlert },
  [ViewState.DASHBOARD_USER]: { label: 'Dashboard', icon: LayoutGrid },
  [ViewState.PHD_ASSISTANCE]: { label: 'Asistencia PHD', icon: Brain },
};

const MODULE_THEME_KEYS: Partial<Record<ViewState, string>> = {
  [ViewState.MODULAR_1]: 'module_theme_modular1',
  [ViewState.MODULAR_2]: 'module_theme_modular2',
  [ViewState.DICTAPHONE]: 'module_theme_dictaphone',
  [ViewState.CHAT_IA]: 'module_theme_chat',
  [ViewState.COMMUNITY]: 'module_theme_community',
  [ViewState.PHD_ASSISTANCE]: 'module_theme_phd',
  [ViewState.PROFILE]: 'module_theme_profile',
  [ViewState.REPORTS]: 'module_theme_reports',
  [ViewState.PHRASES]: 'module_theme_reports',
  [ViewState.CREATE_REPORT]: 'module_theme_reports',
  [ViewState.EDIT_REPORT]: 'module_theme_reports',
  [ViewState.CREATE_PHRASE]: 'module_theme_reports',
  [ViewState.EDIT_PHRASE]: 'module_theme_reports',
  [ViewState.MANAGE_FILTERS]: 'module_theme_reports',
  [ViewState.RECYCLE_BIN]: 'module_theme_reports',
};

const DEFAULT_SECTIONS = [
  { id: 's1', items: [ViewState.REPORTS, ViewState.DASHBOARD_USER] },
  { id: 's2', items: [ViewState.MODULAR_1, ViewState.DICTAPHONE, ViewState.PHD_ASSISTANCE] },
  { id: 's3', items: [ViewState.COMMUNITY, ViewState.CHAT_IA] }
];

const PROFILE_CONFIGS: Record<AppProfile, ViewState[]> = {
  'Basico': [ViewState.REPORTS, ViewState.DICTAPHONE, ViewState.PROFILE, ViewState.DASHBOARD_USER],
  'Medio': [ViewState.REPORTS, ViewState.DICTAPHONE, ViewState.COMMUNITY, ViewState.PROFILE, ViewState.DASHBOARD_USER, ViewState.PHD_ASSISTANCE],
  'Avanzado': [
    ViewState.PROFILE, 
    ViewState.DASHBOARD_USER, 
    ViewState.REPORTS, 
    ViewState.DICTAPHONE, 
    ViewState.MODULAR_1, 
    ViewState.CHAT_IA, 
    ViewState.COMMUNITY,
    ViewState.PHD_ASSISTANCE
  ],
  'Libre': [] 
};

const AppLogo = ({ size = 24, className = "", monochrome = false }: { size?: number, className?: string, monochrome?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="2" y="2" width="9" height="9" rx="2.5" className={monochrome ? "fill-current" : "fill-blue-600"} />
    <rect x="13" y="2" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-40" : "fill-blue-50"} />
    <rect x="2" y="13" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-70" : "fill-indigo-600"} />
    <rect x="13" y="13" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-90" : "fill-purple-500"} />
  </svg>
);

const IconLayoutSingle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
);
const IconLayoutSplit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="18" rx="2" /><rect x="13" y="3" width="8" height="18" rx="2" /></svg>
);
const IconLayoutSplitHorizontal = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="8" rx="2" /><rect x="3" y="13" width="18" height="8" rx="2" /></svg>
);
const IconLayoutThree = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="6" height="18" rx="1" /><rect x="9" y="3" width="6" height="18" rx="1" /><rect x="16" y="3" width="6" height="18" rx="1" /></svg>
);
const IconLayoutFour = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="4.5" height="18" rx="1" /><rect x="7.5" y="3" width="4.5" height="18" rx="1" /><rect x="13" y="3" width="4.5" height="18" rx="1" /><rect x="18.5" y="3" width="4.5" height="18" rx="1" /></svg>
);
const IconLayoutThreeFocus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="4" height="18" rx="1" /><rect x="7" y="3" width="10" height="18" rx="1" /><rect x="18" y="3" width="4" height="18" rx="1" /></svg>
);
const IconLayoutMixed = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5a2 2 0 0 1 2-2h6v18H5a2 2 0 0 1-2-2V5z" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg>
);
const IconLayoutMixedMirror = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <path d="M13 5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V5z" />
  </svg>
);
const IconLayoutGrid = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
  </svg>
);

const LAYOUT_META: Record<ModularLayoutType, { label: string; icon: any }> = {
  'single': { label: 'Simple', icon: IconLayoutSingle },
  'split-vertical': { label: 'Vertical', icon: IconLayoutSplit },
  'split-horizontal': { label: 'Horizontal', icon: IconLayoutSplitHorizontal },
  'split-three': { label: 'Triple', icon: IconLayoutThree },
  'split-four': { label: 'Cuádruple', icon: IconLayoutFour },
  'split-three-focus': { label: 'Enfoque', icon: IconLayoutThreeFocus },
  'mixed': { label: 'Mixto A', icon: IconLayoutMixed },
  'mixed-mirror': { label: 'Mixto B', icon: IconLayoutMixedMirror },
  'grid-2x2': { label: 'Grid 2x2', icon: IconLayoutGrid },
};

interface LayoutDimensions {
  main: number[];
  subLeft?: number[];
  subRight?: number[];
}

const DEFAULT_DIMENSIONS: Record<ModularLayoutType, LayoutDimensions> = {
  'single': { main: [100] },
  'split-vertical': { main: [50, 50] },
  'split-horizontal': { main: [50, 50] },
  'split-three': { main: [33.33, 33.33, 33.33] },
  'split-four': { main: [25, 25, 25, 25] },
  'split-three-focus': { main: [25, 50, 25] },
  'mixed': { main: [50, 50], subRight: [50, 50] },
  'mixed-mirror': { main: [50, 50], subLeft: [50, 50] },
  'grid-2x2': { main: [50, 50], subLeft: [50, 50], subRight: [50, 50] }
};

const DEFAULT_CONFIGURATIONS: WorkspaceConfigurations = {
  'single': [{ id: 0, view: ViewState.REPORTS }],
  'split-vertical': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }],
  'split-horizontal': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }],
  'split-three': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }, { id: 2, view: null }],
  'split-four': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }, { id: 2, view: ViewState.DICTAPHONE }, { id: 3, view: null }],
  'split-three-focus': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }, { id: 2, view: ViewState.DICTAPHONE }],
  'mixed': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }, { id: 2, view: ViewState.DICTAPHONE }],
  'mixed-mirror': [{ id: 0, view: ViewState.MODULAR_1 }, { id: 2, view: ViewState.DICTAPHONE }, { id: 1, view: ViewState.REPORTS }],
  'grid-2x2': [{ id: 0, view: ViewState.REPORTS }, { id: 1, view: ViewState.MODULAR_1 }, { id: 2, view: null }, { id: 3, view: null }]
};

const STATUS_CONFIG: Record<CollaborationStatus, { label: string; color: string; ring: string; iconColor: string }> = {
  'AVAILABLE': { label: 'Disponible', color: 'bg-emerald-500', ring: 'ring-emerald-100', iconColor: 'text-emerald-600' },
  'READING': { label: 'Informando', color: 'bg-blue-500', ring: 'ring-blue-100', iconColor: 'text-blue-600' },
  'ON_CALL': { label: 'De Guardia', color: 'bg-amber-500', ring: 'ring-amber-100', iconColor: 'text-amber-600' },
  'NOT_AVAILABLE': { label: 'No Disponible', color: 'bg-red-500', ring: 'ring-red-100', iconColor: 'text-red-600' },
};

type WorkspaceConfigurations = Record<ModularLayoutType, ModuleSlot[]>;

interface ResizeHandleProps {
  orientation: 'horizontal' | 'vertical';
  onMouseDown: (e: React.MouseEvent) => void;
}

interface ModularWorkspaceProps {
  activeView: ViewState;
  renderModule: (view: ViewState) => React.ReactNode;
  onLogout: () => void;
  user: FirebaseUser | null;
  onNavigate: (view: ViewState) => void;
  isProfileIncomplete: boolean;
  reports: ReportTemplate[];
  isPopOut?: boolean;
  isPinned?: boolean;
  onTogglePin?: () => void;
  windowPersistenceEnabled: boolean;
  onTogglePersistence: () => void;
}

const ResizeHandle: React.FC<ResizeHandleProps> = ({ orientation, onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    className={`z-20 flex-shrink-0 flex justify-center items-center group relative ${orientation === 'horizontal' ? 'w-[8px] -mx-[4px] h-full cursor-col-resize' : 'h-[8px] -my-[4px] w-full cursor-row-resize'}`}
  >
    <div className={`bg-gray-300 group-hover:bg-blue-500 transition-colors duration-200 ${orientation === 'horizontal' ? 'w-[1px] h-full' : 'h-[1px] w-full'}`} />
  </div>
);

const WindowPositionPreview = ({ pos, isDarkMode }: { pos: any, isDarkMode: boolean }) => {
  // Usamos las dimensiones de pantalla guardadas o las actuales como fallback
  const sw = pos.sw || window.screen.width || 1920;
  const sh = pos.sh || window.screen.height || 1080;
  
  // Normalizamos las coordenadas para el preview (proporción 16:9 aprox para el contenedor)
  // El contenedor es de 40x24px
  const x = (pos.x / sw) * 100;
  const y = (pos.y / sh) * 100;
  const w = (pos.w / sw) * 100;
  const h = (pos.h / sh) * 100;

  return (
    <div className={`relative w-10 h-6 rounded-[3px] border overflow-hidden flex-shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
      <div 
        className="absolute bg-blue-500/40 border-[0.5px] border-blue-500 rounded-[1px]"
        style={{
          left: `${Math.max(0, Math.min(90, x))}%`,
          top: `${Math.max(0, Math.min(90, y))}%`,
          width: `${Math.max(10, Math.min(100, w))}%`,
          height: `${Math.max(10, Math.min(100, h))}%`,
        }}
      />
    </div>
  );
};

interface MobileSettingsTabProps {
  initialMobileViews: ViewState[];
  onSave: (newViews: ViewState[]) => void;
  isDarkMode: boolean;
}

const MobileSettingsTab: React.FC<MobileSettingsTabProps> = ({
  initialMobileViews,
  onSave,
  isDarkMode
}) => {
  const [draftMobileViews, setDraftMobileViews] = useState<ViewState[]>(initialMobileViews);
  const [isMobileViewsDirty, setIsMobileViewsDirty] = useState(false);

  const toggleDraftMobileView = (vId: ViewState) => {
    setDraftMobileViews(prev => {
      const next = prev.includes(vId) ? prev.filter(v => v !== vId) : [...prev, vId];
      const isDifferent = next.length !== initialMobileViews.length || 
                          next.some(v => !initialMobileViews.includes(v)) || 
                          initialMobileViews.some(v => !next.includes(v));
      setIsMobileViewsDirty(isDifferent);
      return next;
    });
  };

  return (
    <section className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-2 text-purple-500">
        <Smartphone size={16} />
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Visibilidad en Menú Móvil</h3>
      </div>
      
      <p className="text-[11px] text-gray-400 font-bold leading-relaxed uppercase tracking-wider">
        Selecciona qué módulos clínicos deseas que aparezcan y estén disponibles en el menú de navegación de la visión móvil:
      </p>

      <div className="space-y-2 pl-2">
        {Object.entries(MODULE_META).map(([viewId, meta]) => {
          const vId = viewId as ViewState;
          const isVisibleMobile = draftMobileViews.includes(vId);
          
          return (
            <div key={vId} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isDarkMode ? 'bg-black/10 border-gray-800' : 'bg-white border-gray-100'}`}>
              <div className="flex items-center gap-2">
                <meta.icon size={16} className="text-purple-500" />
                <span className="text-[11px] font-black uppercase tracking-wider">{meta.label}</span>
              </div>
              
              <button 
                onClick={() => toggleDraftMobileView(vId)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${isVisibleMobile ? 'text-purple-500 bg-purple-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                <span>{isVisibleMobile ? 'VISIBLE' : 'OCULTO'}</span>
                {isVisibleMobile ? <CheckCircle size={16} /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-700" />}
              </button>
            </div>
          );
        })}
      </div>

      <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
        <button 
          onClick={() => {
            onSave(draftMobileViews);
            setIsMobileViewsDirty(false);
          }}
          disabled={!isMobileViewsDirty}
          className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all duration-300 ${
            isMobileViewsDirty 
              ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg cursor-pointer animate-pulse' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
          }`}
        >
          <Save size={14} />
          Guardar Configuración Móvil
        </button>
      </div>
    </section>
  );
};

const ModularWorkspace: React.FC<ModularWorkspaceProps> = ({ 
  activeView, 
  renderModule, 
  onLogout, 
  user, 
  onNavigate, 
  isProfileIncomplete,
  reports, 
  isPopOut = false, 
  isPinned = false, 
  onTogglePin,
  windowPersistenceEnabled,
  onTogglePersistence
}) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isDesktopLayoutOpen, setIsDesktopLayoutOpen] = useState(false);
  
  const [layout, setLayout] = useState<ModularLayoutType>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLayout = params.get('layout');
    if (urlLayout && Object.keys(DEFAULT_DIMENSIONS).includes(urlLayout)) return urlLayout as ModularLayoutType;
    const saved = localStorage.getItem('workspace_layout');
    return (saved && Object.keys(DEFAULT_DIMENSIONS).includes(saved)) ? saved as ModularLayoutType : 'split-three-focus';
  });

  const [visibleLayouts, setVisibleLayouts] = useState<ModularLayoutType[]>(() => {
    const saved = localStorage.getItem('modular_visible_layouts');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing visible layouts", e);
      }
    }
    // Default all visible
    return ['single', 'split-vertical', 'split-horizontal', 'split-three', 'split-four', 'split-three-focus', 'mixed', 'mixed-mirror', 'grid-2x2'];
  });

  useEffect(() => {
    localStorage.setItem('modular_visible_layouts', JSON.stringify(visibleLayouts));
  }, [visibleLayouts]);

  const toggleLayoutVisibility = (layoutType: ModularLayoutType) => {
    setVisibleLayouts(prev => {
      if (prev.includes(layoutType)) {
        // No permitir quitar el último layout activo
        if (prev.length <= 1) return prev;
        return prev.filter(l => l !== layoutType);
      }
      return [...prev, layoutType];
    });
  };

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [dimensionsConfig, setDimensionsConfig] = useState<Record<ModularLayoutType, LayoutDimensions>>(() => {
    try {
      const saved = localStorage.getItem('workspace_dimensions_v2');
      return saved ? { ...DEFAULT_DIMENSIONS, ...JSON.parse(saved) } : DEFAULT_DIMENSIONS;
    } catch { return DEFAULT_DIMENSIONS; }
  });

  const [configurations, setConfigurations] = useState<WorkspaceConfigurations>(() => {
    try {
      const saved = localStorage.getItem('workspace_configurations');
      return saved ? { ...DEFAULT_CONFIGURATIONS, ...JSON.parse(saved) } : DEFAULT_CONFIGURATIONS;
    } catch { return DEFAULT_CONFIGURATIONS; }
  });

  const [maximizedSlotId, setMaximizedSlotId] = useState<number | null>(null);

  const [isTextMirroringActive, setIsTextMirroringActive] = useState<boolean>(() => {
    const saved = localStorage.getItem('modular_text_mirroring_active');
    return saved === 'true';
  });

  const toggleTextMirroring = () => {
    const newValue = !isTextMirroringActive;
    setIsTextMirroringActive(newValue);
    localStorage.setItem('modular_text_mirroring_active', String(newValue));
    
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'TEXT_MIRRORING_TOGGLE', enabled: newValue });
    bc.close();
  };

  const [isMasterUser, setIsMasterUser] = useState(() => {
    return localStorage.getItem('master_session_active') === 'true';
  });

  // CERROJO MAESTRO: Una vez que se detecta que es Master, se bloquea en true para evitar "flickering"
  const isMasterLatched = useRef(localStorage.getItem('master_session_active') === 'true');

  useEffect(() => {
    const email = (user?.email || '').toLowerCase();
    const isMasterEmail = email === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
    
    if (isMasterEmail) {
      localStorage.setItem('master_session_active', 'true');
      setIsMasterUser(true);
      isMasterLatched.current = true;
    } else {
      localStorage.removeItem('master_session_active');
      setIsMasterUser(false);
      isMasterLatched.current = false;
    }
  }, [user?.email, user?.uid]);

  if (isMasterUser) isMasterLatched.current = true;

  const isCurrentlyAdmin = useMemo(() => {
    const isMasterEmail = (user?.email || "").toLowerCase().trim() === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
    return isMasterUser || isMasterEmail || (userProfile && userProfile.role === 'ADMIN') || false;
  }, [user, isMasterUser, userProfile]);

  const isUnvalidated = useMemo(() => {
    return !!userProfile && !userProfile.isVerified && !isCurrentlyAdmin;
  }, [userProfile, isCurrentlyAdmin]);

  const [regionalRestrictions, setRegionalRestrictions] = useState<any[]>([]);

  useEffect(() => {
    return subscribeToRegionalRestrictions(setRegionalRestrictions);
  }, []);

  const userDisabledModules = useMemo(() => {
    return getRegionalAccess(userProfile, regionalRestrictions, (isCurrentlyAdmin || !!userProfile?.internalTestingBypass)).disabledModules;
  }, [userProfile, regionalRestrictions, isCurrentlyAdmin]);

  // El selector de niveles (Básico/Medio/Avanzado/Libre) ya no se oculta por restricciones regionales.
  // Las restricciones siguen aplicando igual (bloquean módulos por país/continente); solo se deja de
  // esconder el propio selector, para que el usuario siempre tenga forma de ver y cambiar su nivel.
  const hideProfileSystem = false;

  const [sidebarSections, setSidebarSections] = useState<{id: string, items: ViewState[]}[]>(() => {
    try {
      const saved = localStorage.getItem('workspace_sidebar_sections');
      const profile = localStorage.getItem('app_profile') as AppProfile || 'Libre';
      
      let baseSections = saved ? JSON.parse(saved) : DEFAULT_SECTIONS;
      
      // SIEMPRE FILTRAR SI NO ES LIBRE para respetar el perfil seleccionado
      if (profile !== 'Libre' && PROFILE_CONFIGS[profile]) {
          const allowedItems = PROFILE_CONFIGS[profile];
          baseSections = baseSections.map((s: any) => ({
              ...s,
              items: s.items.filter((item: any) => allowedItems.includes(item))
          })).filter((s: any) => s.items.length > 0);
          
          // Si después de filtrar queda vacío (por ejemplo, por una migración), aplicar el preset completo
          if (baseSections.length === 0) {
              baseSections = [
                  { id: 's1', items: allowedItems.filter(item => [ViewState.PROFILE, ViewState.REPORTS, ViewState.DASHBOARD_USER].includes(item)) },
                  { id: 's2', items: allowedItems.filter(item => [ViewState.MODULAR_1, ViewState.DICTAPHONE, ViewState.PHD_ASSISTANCE].includes(item)) },
                  { id: 's3', items: allowedItems.filter(item => [ViewState.CHAT_IA, ViewState.COMMUNITY].includes(item)) }
              ].filter(section => section.items.length > 0);
          }
      }
      
      // MIGRACIÓN Y LIMPIEZA: Excluir PROFILE y MODULAR_2
      baseSections = baseSections.map((s: any) => ({
        ...s,
        items: s.items.filter((item: any) => item !== ViewState.PROFILE && item !== ViewState.MODULAR_2)
      })).filter((s: any) => s.items.length > 0);

      const hasDashboard = baseSections.some((s: any) => s.items.includes(ViewState.DASHBOARD_USER));
      if (!hasDashboard) {
         // Lo añadimos a la primera sección si no existe en ninguna
         if (baseSections.length > 0) {
            baseSections[0].items.push(ViewState.DASHBOARD_USER);
         } else {
            baseSections = [{ id: 's1', items: [ViewState.DASHBOARD_USER] }];
         }
      }

      if (isMasterUser && !baseSections.some((s: any) => s.id === 'admin')) {
         baseSections.push({ id: 'admin', items: [ViewState.ADMIN_PANEL] });
      }

      // MIGRACIÓN: Asegurar que PHD_ASSISTANCE esté presente para todos los usuarios
      const hasPHD = baseSections.some((s: any) => s.items.includes(ViewState.PHD_ASSISTANCE));
      if (!hasPHD) {
         if (baseSections.length > 1) {
            baseSections[1].items.push(ViewState.PHD_ASSISTANCE);
         } else if (baseSections.length > 0) {
            baseSections[0].items.push(ViewState.PHD_ASSISTANCE);
         } else {
            baseSections = [{ id: 's1', items: [ViewState.PHD_ASSISTANCE] }];
         }
      }

      return baseSections;
    } catch { return DEFAULT_SECTIONS; }
  });

  const [mobileViewsLibre, setMobileViewsLibre] = useState<ViewState[]>(() => {
    try {
      const saved = localStorage.getItem('workspace_mobile_views_libre');
      const views = saved ? JSON.parse(saved) : [ViewState.REPORTS, ViewState.DICTAPHONE, ViewState.COMMUNITY, ViewState.CHAT_IA, ViewState.DASHBOARD_USER, ViewState.PHD_ASSISTANCE];
      return views.filter((v: ViewState) => v !== ViewState.MODULAR_2);
    } catch {
      return [ViewState.REPORTS, ViewState.DICTAPHONE, ViewState.COMMUNITY, ViewState.CHAT_IA, ViewState.DASHBOARD_USER, ViewState.PHD_ASSISTANCE];
    }
  });

  const [staticPositions, setStaticPositions] = useState<Record<string, any>>(() => {
    try {
      const saved = localStorage.getItem('modular_static_positions');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const deleteStaticPosition = (id: string) => {
    setStaticPositions(prev => {
      const next = { ...prev };
      delete next[id];
      localStorage.setItem('modular_static_positions', JSON.stringify(next));
      return next;
    });
  };

  const clearStaticPositions = () => {
    setConfirmModal({
      title: "LIMPIEZA DE VENTANAS",
      message: "¿Seguro que deseas eliminar todas las ventanas móviles registradas?",
      type: 'danger',
      onConfirm: () => {
        localStorage.removeItem('modular_static_positions');
        setStaticPositions({});
        setConfirmModal(null);
      }
    });
  };

  const [appProfile, setAppProfile] = useState<AppProfile>(() => {
    return (localStorage.getItem('app_profile') as AppProfile) || 'Avanzado';
  });

  const [appTheme, setAppTheme] = useState<AppTheme>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTheme = params.get('theme');
    if (urlTheme === 'dark' || urlTheme === 'mixed' || urlTheme === 'day') return urlTheme as AppTheme;

    const saved = localStorage.getItem('app_theme');
    if (saved === 'dark') return 'dark';
    if (saved === 'mixed') return 'mixed';
    if (saved === 'day') return 'day';
    return 'mixed';
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('theme') === 'dark') return true;
    if (params.get('theme') === 'day') return false;

    const theme = localStorage.getItem('app_theme') || 'mixed';
    return theme !== 'day';
  });

  const [moduleThemeTick, setModuleThemeTick] = useState(0);
  useEffect(() => {
    const handler = () => setModuleThemeTick(t => t + 1);
    window.addEventListener('module_theme_changed', handler);
    return () => window.removeEventListener('module_theme_changed', handler);
  }, []);

  const getSlotDarkMode = (viewId: ViewState | null | undefined): boolean => {
    void moduleThemeTick; // fuerza recálculo cuando cambia el tema de cualquier módulo
    if (!viewId) return isDarkMode;
    const key = MODULE_THEME_KEYS[viewId];
    if (!key) return isDarkMode;
    const saved = localStorage.getItem(key);
    if (saved === 'dark') return true;
    if (saved === 'day') return false;
    return isDarkMode;
  };

  const [spaceShortcutEnabled, setSpaceShortcutEnabled] = useState(() => {
    return localStorage.getItem('app_shortcut_space_record') !== 'false';
  });

  const [agentShortcutEnabled, setAgentShortcutEnabled] = useState(() => {
    return localStorage.getItem('app_shortcut_agent_double_a') === 'true';
  });

  const [copyRichTextEnabled, setCopyRichTextEnabled] = useState(() => {
    const saved = localStorage.getItem('modular_copy_rich_text');
    return saved === null ? true : saved === 'true';
  });

  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('modular_enable_notifications') !== 'false';
  });

  useEffect(() => {
    const handleAutomationCommand = async (e: KeyboardEvent) => {
      // Comando Secreto: Ctrl + Alt + P (Enviado por el script de AHK)
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        try {
          const text = await navigator.clipboard.readText();
          if (text && text.trim().length > 0) {
            const candidateReports = reports.map(r => ({ id: r.id, title: r.title }));
            const decision = await selectAgentTemplateAndMode(text, candidateReports);
            onNavigate(ViewState.MODULAR_1);
            setTimeout(() => {
              const bc = new BroadcastChannel('modular_sync_channel');
              bc.postMessage({
                type: 'RAD1_AGENT_AUTO',
                text,
                reportId: decision.selectedReportId,
                fusionMode: decision.fusionMode,
                flexibleMode: decision.flexibleMode
              });
              bc.close();
            }, 500);
          }
        } catch (err) {
          console.error("Error en automatización PhD:", err);
        }
      }
    };

    window.addEventListener('keydown', handleAutomationCommand);
    return () => window.removeEventListener('keydown', handleAutomationCommand);
  }, [onNavigate, reports]);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<'interface' | 'mobile' | 'auto' | 'sync' | 'system'>('interface');
  const [isBackingUpCloud, setIsBackingUpCloud] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState(false);
  const [isGeneratingExtension, setIsGeneratingExtension] = useState(false);
  const [visibleHelp, setVisibleHelp] = useState<Record<string, boolean>>({});

  const handleDownloadExtensionZip = async () => {
    if (isGeneratingExtension) return;
    try {
      setIsGeneratingExtension(true);
      await generateExtensionZip(window.location.origin);
    } catch (err) {
      console.error("Error al generar el plugin PACS:", err);
      alert("No se pudo generar el archivo ZIP de la extensión. Intente nuevamente.");
    } finally {
      setIsGeneratingExtension(false);
    }
  };

  const handleSaveMobileViews = (newViews: ViewState[]) => {
    isProcessingSync.current = true;
    setMobileViewsLibre(newViews);
    localStorage.setItem('workspace_mobile_views_libre', JSON.stringify(newViews));
    localStorage.setItem('workspace_mobile_views_libre_custom', JSON.stringify(newViews));
    
    if (appProfile !== 'Libre') {
      setAppProfile('Libre');
      localStorage.setItem('app_profile', 'Libre');
      localStorage.setItem('workspace_sidebar_sections_libre', JSON.stringify(sidebarSections));
    }

    const now = Date.now();
    localStorage.setItem('workspace_settings_updated', String(now));

    // Sincronizar por canal
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'MOBILE_VIEWS_SYNC', views: newViews });
    bc.close();
    
    setTimeout(() => { 
      performCloudBackup(false);
      isProcessingSync.current = false; 
    }, 200);

    setInfoModal({
      title: "CONFIGURACIÓN GUARDADA",
      message: "Tus preferencias de visibilidad del menú móvil se han guardado con éxito. Se ha procedido a aplicar e indexar la configuración."
    });
  };

  useEffect(() => {
    if (isSettingsOpen) {
      try {
        const saved = localStorage.getItem('modular_static_positions');
        setStaticPositions(saved ? JSON.parse(saved) : {});
      } catch { setStaticPositions({}); }
    }
  }, [isSettingsOpen]);
  
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void; type?: 'danger' | 'info' } | null>(null);
  const [infoModal, setInfoModal] = useState<{ title: string; message: string } | null>(null);

  const [autoPasteEnabled, setAutoPasteEnabled] = useState(() => {
    return localStorage.getItem('modular_auto_paste_enabled') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('modular_auto_paste_enabled', String(autoPasteEnabled));
  }, [autoPasteEnabled]);

  // Lógica de Mensajes Nuevos (Comunidad) - Solo para el badge del menú
  const [hasUnreadCommunity, setHasUnreadCommunity] = useState(false);
  const lastHasNewRef = useRef(false);
  const lastGroupMsgRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;
    
    // Suscripción a chats activos para el badge del menú (sin toasts, ya los maneja App.tsx)
    const unsub = subscribeToActiveChats(user.uid, (chats) => {
      if (activeView === ViewState.COMMUNITY) {
        setHasUnreadCommunity(false);
        lastHasNewRef.current = false;
        localStorage.setItem('modular_community_last_read', Date.now().toString());
        return;
      }

      const lastRead = parseInt(localStorage.getItem('modular_community_last_read') || '0');
      const hasNew = chats.some(chat => 
        chat.lastMessage && 
        chat.lastMessage.senderId !== user.uid && 
        chat.lastMessage.timestamp > lastRead
      );
      
      lastHasNewRef.current = hasNew;
      setHasUnreadCommunity(hasNew);
    });

    return () => unsub();
  }, [user, activeView]);

  // Suscripción a grupos para el badge del menú
  useEffect(() => {
    if (!user) return;
    
    const unsub = subscribeToWorkGroups(user.uid, (groups) => {
      if (activeView === ViewState.COMMUNITY) {
        localStorage.setItem('modular_community_groups_last_read', Date.now().toString());
        return;
      }

      const lastRead = parseInt(localStorage.getItem('modular_community_groups_last_read') || '0');
      const hasNewGroupMsg = groups.some(g => 
        g.lastMessage && 
        g.lastMessage.senderId !== user.uid && 
        g.lastMessage.timestamp > lastRead &&
        !userProfile?.notificationSettings?.mutedGroups?.includes(g.id)
      );

      if (hasNewGroupMsg) {
        setHasUnreadCommunity(true);
      }
    });

    return () => unsub();
  }, [user, activeView, userProfile]);

  useEffect(() => {
    if (activeView === ViewState.COMMUNITY) {
      setHasUnreadCommunity(false);
      localStorage.setItem('modular_community_last_read', Date.now().toString());
    }
  }, [activeView]);

  const toggleHelp = (key: string) => setVisibleHelp(prev => ({...prev, [key]: !prev[key]}));

  const [sidebarPos, setSidebarPos] = useState<SidebarPosition>(() => {
    return (localStorage.getItem('modular_sidebar_pos') as SidebarPosition) || 'left';
  });
  
  useEffect(() => {
    localStorage.setItem('modular_sidebar_pos', sidebarPos);
  }, [sidebarPos]);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
     const params = new URLSearchParams(window.location.search);
     if (params.get('layout') === 'single') return false;
     const saved = localStorage.getItem('modular_sidebar_open');
     return saved === null ? true : saved === 'true';
  });
  
  useEffect(() => {
    localStorage.setItem('modular_sidebar_open', String(isSidebarOpen));
  }, [isSidebarOpen]);
  
  const [draggedOverSlotId, setDraggedOverSlotId] = useState<number | null>(null);
  const [draggingSidebarView, setDraggingSidebarView] = useState<ViewState | null>(null);
  const [dragOverSidebarTarget, setDragOverSidebarTarget] = useState<{sectionIndex: number, itemIndex: number} | null>(null);

  const [isAgentRecording, setIsAgentRecording] = useState(false);
  const [isAgentProcessing, setIsAgentProcessing] = useState(false);
  const [isRemoteLocked, setIsRemoteLocked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const agentRecorderRef = useRef<AudioRecorder | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const themeFirstRun = useRef(true);
  const sidebarFirstRun = useRef(true);
  const mobileViewsFirstRun = useRef(true);
  const profileFirstRun = useRef(true);

  const isProcessingSync = useRef(false);
  const mobileWindowsSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (user && userProfile) {
      // BYPASS TOTAL PARA SUPERUSER
      if (isMasterUser) {
        if (showTermsModal) setShowTermsModal(false);
        return;
      }
      
      const hasAcceptedCurrent = userProfile.termsVersion === TERMS_VERSION;
      if (!hasAcceptedCurrent && !isPopOut) {
        setShowTermsModal(true);
      }
    }
  }, [user, userProfile, isPopOut, isMasterUser, showTermsModal]);

  const handleAcceptTerms = async () => {
    if (!user) return;
    
    // Rescate de identidad: Si el perfil es nulo o incompleto, usamos datos de Auth
    const baseProfile: Partial<UserProfile> = userProfile || {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      role: 'RESIDENT', // Por defecto para nuevos si no hay perfil
      status: 'AVAILABLE',
      isVerified: false,
      specialties: [],
      updatedAt: Date.now()
    };

    const updatedProfile: UserProfile = {
      ...baseProfile,
      uid: user.uid, // Forzamos UID correcto
      email: baseProfile.email || user.email || '',
      displayName: baseProfile.displayName || user.displayName || 'Usuario MODULAR',
      termsAcceptedAt: Date.now(),
      termsVersion: TERMS_VERSION,
      updatedAt: Date.now()
    } as UserProfile;
    
    try {
      console.log("Intentando guardar aceptación de términos para:", user.uid);
      await saveUserProfile(updatedProfile);
      console.log("Aceptación de términos guardada con éxito.");
      setShowTermsModal(false);
    } catch (e: any) {
      console.error("Error al guardar aceptación de términos:", e);
      // Extraer mensaje de error si es un JSON de FirestoreErrorInfo
      let displayError = 'Error de conexión con el servidor';
      try {
        const parsed = JSON.parse(e.message);
        if (parsed.error) displayError = `Error de Seguridad: ${parsed.error}`;
      } catch {
        displayError = e.message || displayError;
      }
      alert("No se han podido guardar sus preferencias. Por favor, asegúrese de que su perfil profesional esté completo y vuelva a intentarlo.");
    }
  };
  const handleStatusChange = async (newStatus: CollaborationStatus) => {
    if (!user || !userProfile) return;
    setIsStatusDropdownOpen(false);
    
    const updatedProfile: UserProfile = {
      ...userProfile,
      status: newStatus,
      updatedAt: Date.now()
    };
    
    try {
      await saveUserProfile(updatedProfile);
    } catch (e) {
      console.error("Fallo al actualizar estatus de disponibilidad:", e);
    }
  };

  const handleToggleAgent = useCallback(async () => {
    console.log("[Agent] handleToggleAgent called. Current state:", { isAgentRecording, isAgentProcessing });
    if (isAgentProcessing || isRemoteLocked) return;
    
    // Función interna para procesar el texto final del Agente
    const processFinalAgentText = async (transcript: string) => {
      if (!transcript || !transcript.trim()) {
        console.log("[Agent] Empty transcript, skipping process");
        return;
      }

      console.log("[Agent] Processing transcript with Lite model:", transcript.length, "chars");
      setIsAgentProcessing(true);
      
      try {
        const cmd = transcript.toLowerCase();
        
        // --- ROUTER DE COMANDOS DE VOZ PARA UI ---
        // Layouts
        if (cmd.includes("layout vertical") || cmd.includes("dividir vertical")) { setLayout('split-vertical'); setIsAgentProcessing(false); return; }
        if (cmd.includes("layout horizontal") || cmd.includes("dividir horizontal")) { setLayout('split-horizontal'); setIsAgentProcessing(false); return; }
        if (cmd.includes("layout tres") || cmd.includes("triple vista") || cmd.includes("layout tres focos")) { setLayout('split-three-focus'); setIsAgentProcessing(false); return; }
        if (cmd.includes("layout mixto") || cmd.includes("layout tres mixto")) { setLayout('mixed'); setIsAgentProcessing(false); return; }
        if (cmd.includes("layout rejilla") || cmd.includes("cuatro vistas") || cmd.includes("layout cuadrícula")) { setLayout('grid-2x2'); setIsAgentProcessing(false); return; }
        if (cmd.includes("layout único") || cmd.includes("vista única") || cmd.includes("cerrar todo") || cmd.includes("layout único")) { setLayout('single'); setIsAgentProcessing(false); return; }
        
        // Navegación
        if (cmd.includes("abre dictáfono") || cmd.includes("ir a dictáfono")) { onNavigate(ViewState.DICTAPHONE); setIsAgentProcessing(false); return; }
        if (cmd.includes("abre chat") || cmd.includes("ir a consulta")) { onNavigate(ViewState.CHAT_IA); setIsAgentProcessing(false); return; }
        if (cmd.includes("abre asistencia ph") || cmd.includes("segunda lectura")) { onNavigate(ViewState.PHD_ASSISTANCE); setIsAgentProcessing(false); return; }
        if (cmd.includes("abre perfil") || cmd.includes("perfil de usuario")) { onNavigate(ViewState.PROFILE); setIsAgentProcessing(false); return; }
        
        // Configuración
        if (cmd.includes("modo libre") || cmd.includes("perfil libre")) { setAppProfile('Libre'); setIsAgentProcessing(false); return; }
        if (cmd.includes("restablecer layout") || cmd.includes("limpiar espacios")) { resetLayout(); setIsAgentProcessing(false); return; }

        // Si no es un comando de UI: Agente Inteligente — elige plantilla + modo, y fusiona automáticamente en Modular-1
        const candidateReports = reports.map(r => ({ id: r.id, title: r.title }));
        const decision = await selectAgentTemplateAndMode(transcript, candidateReports);
        console.log("[Agent] Decisión del agente:", decision);

        onNavigate(ViewState.MODULAR_1);

        // Breve espera para asegurar que Modular1Screen esté montado antes de enviarle los datos
        setTimeout(() => {
          const bc = new BroadcastChannel('modular_sync_channel');
          bc.postMessage({
            type: 'RAD1_AGENT_AUTO',
            text: transcript,
            reportId: decision.selectedReportId,
            fusionMode: decision.fusionMode,
            flexibleMode: decision.flexibleMode
          });
          bc.close();
        }, 500);
      } catch (e) {
        console.error("Error procesando dictado del agente:", e);
      } finally {
        setIsAgentProcessing(false);
      }
    };

    if (isAgentRecording) {
      console.log("[Agent] Stopping agent recording...");
      setIsAgentRecording(false);
      setIsAgentProcessing(true); 
      
      const bc = new BroadcastChannel('modular_sync_channel');
      bc.postMessage({ type: 'RECORDING_STOP', senderId: TAB_ID });
      bc.close();

      try {
        if (agentRecorderRef.current) {
          const recorder = agentRecorderRef.current;
          agentRecorderRef.current = null;
          
          const { blob } = await recorder.stop();
          console.log("[Agent] Audio captured, size:", blob.size);
          
          // Comprimir audio igual que en Dictáfono/Modular-1
          const { blob: compressedBlob, mimeType } = await compressAudio(blob);
          const base64Audio = await blobToBase64(compressedBlob);
          
          // Transcribir usando el modelo Lite (default en transcribeAudio)
          console.log("[Agent] Transcribing audio...");
          const transcript = await transcribeAudio(base64Audio, mimeType);
          console.log("[Agent] Transcription received:", transcript);
          
          await processFinalAgentText(transcript);
        }
      } catch (e) {
        console.error("Error Agente al detener grabación:", e);
        setIsAgentProcessing(false);
      }
    } else {
      console.log("[Agent] Starting agent (Standard Audio Pipeline)...");
      try {
        setIsAgentProcessing(true);
        agentRecorderRef.current = new AudioRecorder();
        await agentRecorderRef.current.start();
        
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'RECORDING_START', senderId: TAB_ID });
        bc.close();

        setIsAgentRecording(true);
      } catch (e) { 
        console.error("Error al activar Agente:", e);
        setIsAgentRecording(false);
      } finally {
        setIsAgentProcessing(false);
      }
    }
  }, [isAgentProcessing, isAgentRecording, reports, onNavigate]);

  useEffect(() => {
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.onmessage = (event) => {
      if (event.data.type === 'RECORDING_START' && event.data.senderId !== TAB_ID) {
        setIsRemoteLocked(true);
      } else if (event.data.type === 'RECORDING_STOP' && event.data.senderId !== TAB_ID) {
        setIsRemoteLocked(false);
      }
      
      if (event.data.type === 'TOGGLE_AGENT') {
        handleToggleAgent();
      } else if (event.data.type === 'REQUEST_AGENT_STATUS') {
        bc.postMessage({ type: 'AGENT_STATUS', recording: isAgentRecording, processing: isAgentProcessing });
      } else if (event.data.type === 'TRIGGER_AUTO_PASTE' && autoPasteEnabled) {
        // Reenviar a la extensión si está habilitado
        window.postMessage({ type: 'MODULAR_PASTE_TO_EXT', text: event.data.text }, '*');
      }
      
      isProcessingSync.current = true;
      if (event.data.type === 'CLOSE_SECONDARY_WINDOWS') {
        const params = new URLSearchParams(window.location.search);
        const isSecondaryWindow = !!params.get('instanceId');
        if (isSecondaryWindow) {
          window.close();
        }
      }
      if (event.data.type === 'SIDEBAR_SYNC') {
        setSidebarSections(event.data.sections);
      }
      if (event.data.type === 'PROFILE_SYNC') {
        setAppProfile(event.data.profile);
      }
      if (event.data.type === 'CONFIGURATIONS_SYNC') {
        setConfigurations(event.data.configurations);
      }
      if (event.data.type === 'MOBILE_VIEWS_SYNC') {
        setMobileViewsLibre(event.data.views);
      }
      if (event.data.type === 'TEXT_MIRRORING_TOGGLE') {
        setIsTextMirroringActive(event.data.enabled);
      }
      if (event.data.type === 'MIRRORED_TEXT_RECEIVED') {
        window.dispatchEvent(new CustomEvent('mirrored_text_update', { 
          detail: { text: event.data.text, status: event.data.status } 
        }));
      }
      setTimeout(() => { isProcessingSync.current = false; }, 100);
    };
    return () => bc.close();
  }, [handleToggleAgent, isAgentRecording, isAgentProcessing, autoPasteEnabled]);

  useEffect(() => {
    localStorage.setItem('workspace_sidebar_sections', JSON.stringify(sidebarSections));
    
    if (appProfile === 'Libre') {
       localStorage.setItem('workspace_sidebar_sections_libre', JSON.stringify(sidebarSections));
    }

    if (!isProcessingSync.current && !isPopOut && !sidebarFirstRun.current) {
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'SIDEBAR_SYNC', sections: sidebarSections });
        bc.close();
    }
    sidebarFirstRun.current = false;
  }, [sidebarSections, appProfile, isPopOut]);

  useEffect(() => {
    localStorage.setItem('workspace_mobile_views_libre', JSON.stringify(mobileViewsLibre));
    if (appProfile === 'Libre') {
      localStorage.setItem('workspace_mobile_views_libre_custom', JSON.stringify(mobileViewsLibre));
    }
    if (!isProcessingSync.current && !isPopOut && !mobileViewsFirstRun.current) {
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'MOBILE_VIEWS_SYNC', views: mobileViewsLibre });
        bc.close();
    }
    mobileViewsFirstRun.current = false;
  }, [mobileViewsLibre, appProfile, isPopOut]);

  const handleSaveLibreConfig = () => {
    if (appProfile !== 'Libre') return;
    localStorage.setItem('workspace_sidebar_sections_libre', JSON.stringify(sidebarSections));
    localStorage.setItem('workspace_mobile_views_libre_custom', JSON.stringify(mobileViewsLibre));
    
    const now = Date.now();
    localStorage.setItem('workspace_settings_updated', String(now));
    
    // Respaldar inmediatamente en la nube (silencioso)
    performCloudBackup(false);

    setInfoModal({
      title: "CONFIGURACIÓN GUARDADA",
      message: "Tus ajustes de interfaz (Web y Móvil) para el Modo Libre han sido guardados correctamente."
    });
  };

  useEffect(() => {
    if (appProfile) {
      localStorage.setItem('app_profile', appProfile);
      if (!isProcessingSync.current && !isPopOut && !profileFirstRun.current) {
          const bc = new BroadcastChannel('modular_sync_channel');
          bc.postMessage({ type: 'PROFILE_SYNC', profile: appProfile });
          bc.close();
      }
      profileFirstRun.current = false;
    }
  }, [appProfile, isPopOut]);

  useEffect(() => {
    const handleInterfaceReady = () => {
      const savedLayout = localStorage.getItem('workspace_layout');
      const savedConfigs = localStorage.getItem('workspace_configurations');
      const savedDimensions = localStorage.getItem('workspace_dimensions_v2');
      const savedProfile = localStorage.getItem('app_profile');
      const savedTheme = localStorage.getItem('app_theme');
      const savedMobileViews = localStorage.getItem('workspace_mobile_views_libre');
      const savedSections = localStorage.getItem('workspace_sidebar_sections');

      if (savedLayout && Object.keys(DEFAULT_DIMENSIONS).includes(savedLayout)) setLayout(savedLayout as ModularLayoutType);
      if (savedConfigs) setConfigurations(JSON.parse(savedConfigs));
      if (savedDimensions) setDimensionsConfig(JSON.parse(savedDimensions));
      if (savedProfile) setAppProfile(savedProfile as AppProfile);
      if (savedTheme) setAppTheme(savedTheme as AppTheme);

      if (savedMobileViews) {
        try {
          let views = JSON.parse(savedMobileViews);
          if (Array.isArray(views)) {
            views = views.filter((v: any) => v !== ViewState.MODULAR_2);
            setMobileViewsLibre(views);
          }
        } catch (e) {
          console.error("Error parsing saved mobile views", e);
        }
      }

      if (savedSections) {
        try {
          const parsed = JSON.parse(savedSections);
          if (Array.isArray(parsed)) {
            setSidebarSections(parsed);
          }
        } catch (e) {
          console.error("Error parsing sidebar sections", e);
        }
      }
    };

    const handleGlobalThemeUpdate = () => {
        const currentTheme = localStorage.getItem('app_theme') as AppTheme;
        if (currentTheme && currentTheme !== appTheme) {
            themeFirstRun.current = true; 
            setAppTheme(currentTheme);
        }
    };

    window.addEventListener('modular_interface_ready', handleInterfaceReady);
    window.addEventListener('app_theme_changed', handleGlobalThemeUpdate);
    return () => {
        window.removeEventListener('modular_interface_ready', handleInterfaceReady);
        window.removeEventListener('app_theme_changed', handleGlobalThemeUpdate);
    };
  }, [appTheme]);

  const toggleModuleVisibility = (viewId: ViewState, platform: 'web' | 'mobile') => {
    const now = Date.now();
    localStorage.setItem('workspace_settings_updated', String(now));

    if (platform === 'web') {
      setSidebarSections(prev => {
        const exists = prev.some(s => s.items.includes(viewId));
        if (exists) {
          return prev.map(s => ({
            ...s,
            items: s.items.filter(item => item !== viewId)
          })).filter(s => s.items.length > 0);
        } else {
          // Añadir a la primera sección por defecto
          if (prev.length > 0) {
            return prev.map((s, i) => i === 0 ? { ...s, items: [...s.items, viewId] } : s);
          } else {
            return [{ id: 's1', items: [viewId] }];
          }
        }
      });
    } else {
      setMobileViewsLibre(prev => {
        const next = prev.includes(viewId) ? prev.filter(v => v !== viewId) : [...prev, viewId];
        return next;
      });
    }

    // Respaldar inmediatamente en la nube (silencioso)
    setTimeout(() => {
      performCloudBackup(false);
    }, 200);
  };

  const performCloudBackup = useCallback(async (manual = false) => {
    if (!user || user.uid === 'guest_dev_offline') return;
    if (manual) setIsBackingUpCloud(true);
    try {
      const keysToSync = [
        'workspace_layout', 'workspace_dimensions_v2', 'workspace_configurations',
        'workspace_sidebar_sections', 'workspace_sidebar_sections_libre', 'workspace_mobile_views_libre', 'app_profile', 'app_theme',
        'app_shortcut_space_record', 'app_shortcut_agent_double_a', 'modular_copy_rich_text', 'modular_window_persistence',
        'modular_auto_clear_reports', 'modular_auto_clear_phrases',
        'modular_live_registry', 'modular_static_positions', 'modular_last_view', 'dictaphone_mic_pos',
        'dictaphone_autoCopy', 'dictaphone_autoStop', 'dictaphone_silence', 'dictaphone_panel_proportions'
      ];
      const settings: Record<string, string> = {};
      keysToSync.forEach(key => {
        const val = localStorage.getItem(key);
        if (val !== null) settings[key] = val;
      });
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('module_theme_')) {
          const val = localStorage.getItem(key);
          if (val !== null) settings[key] = val;
        }
      }
      const uiState: UIState = { lastUpdated: Date.now(), settings };
      await saveUIState(user.uid, uiState);
      if (manual) {
        setBackupSuccess(true);
        setTimeout(() => setBackupSuccess(false), 3000);
      }
    } catch (e) {
      console.error("Cloud backup error:", e);
    } finally {
      if (manual) setIsBackingUpCloud(false);
    }
  }, [user]);

  const handleHardReset = async () => {
    setConfirmModal({
      title: "REINICIO MAESTRO",
      message: "¿Seguro que deseas reiniciar el sistema? Se perderán todas las configuraciones locales de la interfaz.",
      type: 'danger',
      onConfirm: async () => {
        if ('serviceWorker' in navigator) {
          try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) { await registration.unregister(); }
          } catch (e) { console.warn("SW Unregister error", e); }
        }
        const keysToRemove = [
          'workspace_layout', 'workspace_dimensions_v2', 'workspace_configurations',
          'workspace_sidebar_sections', 'workspace_sidebar_sections_libre', 'modular_live_registry', 'modular_static_positions', 'modular_last_view',
          'dictaphone_panel_proportions', 'accordion_open_state', 'modular_help_viewed'
        ];
        keysToRemove.forEach(key => localStorage.removeItem(key));
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('module_theme_')) { localStorage.removeItem(key); }
        }
        setConfirmModal(null);
        setTimeout(() => { window.location.replace(window.location.origin + window.location.pathname); }, 500);
      }
    });
  };


  useEffect(() => {
    if (!user || user.uid === 'guest_dev_offline' || !isTextMirroringActive || isPopOut) return;

    const startTime = Date.now() - 30000; // 30 segundos de margen para desincronización de relojes
    const unsubscribe = subscribeToTextStream(user.uid, (data) => {
      if (data && data.timestamp) {
        console.log("Mirroring Filter Check:", { dataTimestamp: data.timestamp, startTime, diff: data.timestamp - startTime });
        if (data.timestamp > startTime) {
          console.log("Mirroring: Broadcasting text to windows...");
          // Enviar a través de BroadcastChannel para que todas las ventanas lo reciban
          const bc = new BroadcastChannel('modular_sync_channel');
          bc.postMessage({ type: 'MIRRORED_TEXT_RECEIVED', text: data.text, status: data.status });
          bc.close();
          
          // También manejarlo localmente si esta es la ventana principal
          window.dispatchEvent(new CustomEvent('mirrored_text_update', { 
            detail: { text: data.text, status: data.status, timestamp: data.timestamp } 
          }));
        } else {
          console.log("Mirroring: Ignored old text (before activation).");
        }
      }
    });

    return () => unsubscribe();
  }, [user, isTextMirroringActive, isPopOut]);

  useEffect(() => {
    if (!user || user.uid === 'guest_dev_offline') return;
    const IDLE_LIMIT = 30 * 60 * 1000;
    const interval = setInterval(() => performCloudBackup(false), IDLE_LIMIT);
    return () => clearInterval(interval);
  }, [user, performCloudBackup]);

  useEffect(() => {
    if (user) {
      const unsubscribe = subscribeToUserProfile(user.uid, async (profile) => {
        // Lógica de supervivencia y auto-sanación para el Master User
        const isMasterEmail = (user.email || "").toLowerCase().trim() === 'dr.francisco.reyes@gmail.com';
        
        if (isMasterEmail && profile && (!profile.isVerified || profile.role !== 'ADMIN')) {
          console.log("[ModularWorkspace] Master User detectado. Forzando validación y rol ADMIN...");
          const healedProfile: UserProfile = {
            ...profile,
            isVerified: true,
            role: 'ADMIN',
            updatedAt: Date.now()
          };
          await saveUserProfile(healedProfile);
          // El callback se volverá a disparar con el perfil sanado
          return;
        }
        
        setUserProfile(profile);
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    const isAuthorizedAdmin = isMasterUser || (userProfile && userProfile.role === 'ADMIN');
    if (isAuthorizedAdmin) {
      setSidebarSections(prev => {
        if (!prev.some(s => s.id === 'admin')) {
          return [...prev, { id: 'admin', items: [ViewState.ADMIN_PANEL] }];
        }
        return prev;
      });
    } else {
      setSidebarSections(prev => {
        if (prev.some(s => s.id === 'admin')) {
          return prev.filter(s => s.id !== 'admin');
        }
        return prev;
      });
    }
  }, [isMasterUser, userProfile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) setIsStatusDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('layout')) localStorage.setItem('workspace_layout', layout);
    setMaximizedSlotId(null);
  }, [layout]);

  const dimensionsSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { 
    if (dimensionsSaveTimeoutRef.current) clearTimeout(dimensionsSaveTimeoutRef.current);
    dimensionsSaveTimeoutRef.current = setTimeout(() => {
        localStorage.setItem('workspace_dimensions_v2', JSON.stringify(dimensionsConfig)); 
    }, 500);
    return () => { if (dimensionsSaveTimeoutRef.current) clearTimeout(dimensionsSaveTimeoutRef.current); };
  }, [dimensionsConfig]);

  useEffect(() => {
    localStorage.setItem('workspace_configurations', JSON.stringify(configurations));
    
    if (!isProcessingSync.current) {
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'CONFIGURATIONS_SYNC', configurations });
        bc.close();
    }
  }, [configurations]);

  const applyPresetProfile = (profile: AppProfile) => {
    isProcessingSync.current = true;
    setAppProfile(profile);
    if (profile !== 'Libre') {
        const allowedItems = PROFILE_CONFIGS[profile];
        
        // Sincronizar también la visibilidad móvil para perfiles preestablecidos
        setMobileViewsLibre(allowedItems);
        
        const newSections = [
            { id: 's1', items: allowedItems.filter(item => [ViewState.PROFILE, ViewState.REPORTS, ViewState.DASHBOARD_USER].includes(item)) },
            { id: 's2', items: allowedItems.filter(item => [ViewState.MODULAR_1, ViewState.DICTAPHONE, ViewState.PHD_ASSISTANCE].includes(item)) },
            { id: 's3', items: allowedItems.filter(item => [ViewState.CHAT_IA, ViewState.COMMUNITY].includes(item)) }
        ].filter(section => section.items.length > 0); 
        const isAuthorizedAdmin = isMasterUser || (userProfile && userProfile.role === 'ADMIN');
        if (isAuthorizedAdmin) newSections.push({ id: 'admin', items: [ViewState.ADMIN_PANEL] });
        setSidebarSections(newSections);
    } else {
        const savedLibre = localStorage.getItem('workspace_sidebar_sections_libre');
        if (savedLibre) {
          setSidebarSections(JSON.parse(savedLibre));
        } else {
          localStorage.setItem('workspace_sidebar_sections_libre', JSON.stringify(sidebarSections));
          setSidebarSections([...sidebarSections]);
        }
        
        const savedMobileLibre = localStorage.getItem('workspace_mobile_views_libre_custom');
        if (savedMobileLibre) {
          setMobileViewsLibre(JSON.parse(savedMobileLibre));
        }
    }

    const now = Date.now();
    localStorage.setItem('workspace_settings_updated', String(now));

    setTimeout(() => { 
      performCloudBackup(false);
      isProcessingSync.current = false; 
    }, 200);
  };

  
  useEffect(() => { 
    localStorage.setItem('app_theme', appTheme); 
    setIsDarkMode(appTheme !== 'day');
    window.dispatchEvent(new Event('app_theme_changed'));
    
    if (!themeFirstRun.current) {
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'THEME_CHANGED', theme: appTheme });
        bc.close();
    } else {
        themeFirstRun.current = false;
    }
  }, [appTheme]);

  useEffect(() => { localStorage.setItem('app_shortcut_space_record', String(spaceShortcutEnabled)); window.dispatchEvent(new Event('app_shortcuts_changed')); }, [spaceShortcutEnabled]);
  useEffect(() => { localStorage.setItem('app_shortcut_agent_double_a', String(agentShortcutEnabled)); }, [agentShortcutEnabled]);
  useEffect(() => { localStorage.setItem('modular_copy_rich_text', String(copyRichTextEnabled)); window.dispatchEvent(new Event('app_shortcuts_changed')); }, [copyRichTextEnabled]);

  const handleGlobalThemeChange = (newTheme: AppTheme) => {
    Object.keys(localStorage).forEach(key => { if (key.startsWith('module_theme_')) localStorage.removeItem(key); });
    setAppTheme(newTheme);
  };

  const changeLayout = (newLayout: ModularLayoutType) => setLayout(newLayout);

  const resetLayout = () => {
    setLayout('split-three-focus');
    setConfigurations(DEFAULT_CONFIGURATIONS);
    setDimensionsConfig(DEFAULT_DIMENSIONS);
    localStorage.removeItem('workspace_layout');
    localStorage.removeItem('workspace_configurations');
    localStorage.removeItem('workspace_dimensions_v2');
  };

  // --- MOTOR DE RESTAURACIÓN AUTOMÁTICO (LIMPIO) v12.0 ---
  const handleOpenMobileView = () => {
    try {
      const origin = window.location.origin;
      const path = window.location.pathname;
      const urlBase = origin + (path.endsWith('/') ? path : path + '/');
      
      const staticRegistryRaw = localStorage.getItem('modular_static_positions');

      if (!staticRegistryRaw) {
        setInfoModal({ title: "SISTEMA", message: "No hay ventanas registradas para restaurar. Abre una ventana y ánclala con la chincheta para guardarla." });
        return;
      }

      const positions: Record<string, any> = JSON.parse(staticRegistryRaw);
      const pinnedInstances = Object.values(positions).filter(p => p.isPinned);
      
      if (pinnedInstances.length === 0) {
        setInfoModal({ title: "SISTEMA", message: "Hay ventanas en el registro pero ninguna está ANCLADA. Solo se restauran las ventanas con chincheta activa." });
        return;
      }

      pinnedInstances.forEach((p, index) => {
        const feat = `width=${p.w},height=${p.h},top=${p.y},left=${p.x},menubar=no,status=no,toolbar=no,location=no,resizable=yes`;
        const restoreUrl = `${urlBase}?layout=single&view=${p.view}&theme=${appTheme}&instanceId=${p.instanceId}`;
        
        // Log silencioso en consola para seguimiento técnico
        console.log(`Restaurando Ventana ${index + 1}: ${p.view} en ID ${p.instanceId}`);
        window.open(restoreUrl, p.instanceId, feat);
      });
      
      const bc = new BroadcastChannel('modular_sync_channel');
      bc.postMessage({ type: 'REQUEST_ALL_POSITIONS' });
      setTimeout(() => bc.close(), 100);

    } catch (e: any) {
      console.error(`Error en restauración: ${e.message}`);
    }
  };

  const handleSimplePopOut = () => {
    const origin = window.location.origin;
    const path = window.location.pathname;
    const urlBase = origin + (path.endsWith('/') ? path : path + '/');
    const url = `${urlBase}?layout=single&view=${activeView}&theme=${appTheme}&force=true`;
    
    const feat = "width=414,height=896,menubar=no,status=no,location=no,toolbar=no";
    // Usar un nombre único basado en timestamp para asegurar que el navegador abra una ventana nueva cada vez
    window.open(url, `MODULAR_POP_${Date.now()}`, feat);
  };

  const isViewInAnySlot = (viewId: ViewState) => {
    const currentSlots = configurations[layout] || [];
    
    // Si la vista está directamente en un slot activo, se resalta
    const isInSlot = currentSlots.some(slot => slot.view === viewId);
    if (isInSlot) return true;

    // Caso especial para la "Biblioteca" y sus sub-vistas
    if (viewId === ViewState.REPORTS) {
       const libraryViews = [ViewState.REPORTS, ViewState.PHRASES, ViewState.CREATE_REPORT, ViewState.EDIT_REPORT, ViewState.CREATE_PHRASE, ViewState.EDIT_PHRASE, ViewState.MANAGE_FILTERS, ViewState.RECYCLE_BIN];
       // Si algún slot tiene cualquier vista perteneciente a la categoría Biblioteca
       return currentSlots.some(slot => libraryViews.includes(slot.view as any));
    }
    
    return false;
  };


  const togglePersistence = () => { 
    onTogglePersistence();
  };

  const updateSlot = (slotId: number, view: ViewState | null) => {
    setConfigurations(prev => {
      const currentSlots = [...(prev[layout] || [])];
      const slotIndex = currentSlots.findIndex(s => s.id === slotId);
      if (slotIndex >= 0) currentSlots[slotIndex] = { ...currentSlots[slotIndex], view };
      else currentSlots.push({ id: slotId, view });
      return { ...prev, [layout]: currentSlots };
    });
  };

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (activeView === ViewState.ADMIN_PANEL) { setLayout('single'); setConfigurations(prev => ({ ...prev, single: [{ id: 0, view: ViewState.ADMIN_PANEL }] })); return; }
    if (layout === 'single') setConfigurations(prev => ({ ...prev, single: [{ id: 0, view: activeView }] }));
  }, [activeView, layout]);

  const handleSidebarDragStart = (e: React.DragEvent, sectionIndex: number, itemIndex: number, viewId: ViewState) => {
    setDraggingSidebarView(viewId);
    e.dataTransfer.setData('moduleView', viewId);
    e.dataTransfer.setData('sourceSection', sectionIndex.toString());
    e.dataTransfer.setData('sourceItem', itemIndex.toString());
    e.dataTransfer.setData('originType', 'sidebar');
    e.dataTransfer.effectAllowed = 'copyMove';

    // Implementación del "Fantasma del Nombre" para drag & drop
    const meta = MODULE_META[viewId];
    if (meta) {
      const ghost = document.createElement('div');
      ghost.id = 'drag-ghost-label';
      ghost.innerText = meta.label.toUpperCase();
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      ghost.style.left = '-1000px';
      ghost.style.padding = '8px 16px';
      
      // Estilo adaptativo según el tema (Día vs Noche)
      if (appTheme === 'day') {
        ghost.style.background = '#ffffff';
        ghost.style.color = '#2563eb'; // Azul clínico intenso
        ghost.style.border = '1px solid #e5e7eb';
        ghost.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
      } else {
        ghost.style.background = '#1e1b4b'; // Indigo oscuro
        ghost.style.color = '#fbbf24'; // Ámbar
        ghost.style.border = '1px solid rgba(251, 191, 36, 0.3)';
        ghost.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.4)';
      }
      
      ghost.style.borderRadius = '8px';
      ghost.style.fontSize = '10px';
      ghost.style.fontWeight = '900';
      ghost.style.letterSpacing = '0.1em';
      ghost.style.zIndex = '-100';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 10, 10);
      
      // Limpieza inmediata del DOM (setDragImage hace una copia interna)
      setTimeout(() => {
        if (document.body.contains(ghost)) {
          document.body.removeChild(ghost);
        }
      }, 0);
    }
  };

  const handleSidebarDragEnd = () => { 
    setDraggingSidebarView(null); 
    setDragOverSidebarTarget(null); 
    setDraggedOverSlotId(null); 
  };

  const handleSidebarDragOverItem = (e: React.DragEvent, sectionIndex: number, itemIndex: number) => { 
    e.preventDefault(); 
    if (dragOverSidebarTarget?.sectionIndex !== sectionIndex || dragOverSidebarTarget?.itemIndex !== itemIndex) {
      setDragOverSidebarTarget({ sectionIndex, itemIndex }); 
    }
  };

  const handleSidebarDropOnItem = (e: React.DragEvent, targetSectionIndex: number, targetItemIndex: number) => {
    e.preventDefault(); 
    e.stopPropagation();
    const originType = e.dataTransfer.getData('originType');
    if (originType !== 'sidebar') return;
    const sourceSectionIndex = parseInt(e.dataTransfer.getData('sourceSection'));
    const sourceItemIndex = parseInt(e.dataTransfer.getData('sourceItem'));
    const viewId = e.dataTransfer.getData('moduleView') as ViewState;
    if (sourceSectionIndex === targetSectionIndex && sourceItemIndex === targetItemIndex) return;
    const newSections = sidebarSections.map(s => ({ ...s, items: [...s.items] }));
    newSections[sourceSectionIndex].items.splice(sourceItemIndex, 1);
    newSections[targetSectionIndex].items.splice(targetItemIndex, 0, viewId);
    setSidebarSections(newSections);
    setDragOverSidebarTarget(null); 
    setDraggingSidebarView(null);
  };

  const handleSidebarDropOnEmptySection = (e: React.DragEvent, sectionIndex: number) => {
    e.preventDefault();
    const originType = e.dataTransfer.getData('originType');
    if (originType !== 'sidebar') return;
    const sourceSectionIndex = parseInt(e.dataTransfer.getData('sourceSection'));
    const sourceItemIndex = parseInt(e.dataTransfer.getData('sourceItem'));
    const viewId = e.dataTransfer.getData('moduleView') as ViewState;
    const newSections = sidebarSections.map(s => ({ ...s, items: [...s.items] }));
    newSections[sourceSectionIndex].items.splice(sourceItemIndex, 1);
    newSections[sectionIndex].items.push(viewId);
    setSidebarSections(newSections);
    setDragOverSidebarTarget(null); setDraggingSidebarView(null);
  };

  const handleDragOverSlot = (e: React.DragEvent, slotId: number) => { 
    e.preventDefault(); 
    e.dataTransfer.dropEffect = 'copy'; 
    if (draggedOverSlotId !== slotId) setDraggedOverSlotId(slotId); 
  };
  
  const handleDropOnSlot = (e: React.DragEvent, slotId: number) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setDraggedOverSlotId(null); 
    const view = e.dataTransfer.getData('moduleView') as ViewState; 
    if (view && MODULE_META[view]) {
        updateSlot(slotId, view); 
        if (layout === 'single') onNavigate(view);
    }
  };

  const handleResizeStart = (e: React.MouseEvent, index: number, context: keyof LayoutDimensions, orientation: 'horizontal' | 'vertical') => {
    e.preventDefault(); e.stopPropagation();
    const startPos = orientation === 'horizontal' ? e.clientX : e.clientY;
    const container = (e.currentTarget as HTMLElement).parentElement?.getBoundingClientRect();
    if (!container) return;
    const totalSizePixels = orientation === 'horizontal' ? container.width : container.height;
    const activeDimensions = dimensionsConfig[layout];
    const initialSizes = [...(activeDimensions[context] || [])]; 
    document.body.style.cursor = orientation === 'horizontal' ? 'col-resize' : 'row-resize';
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const currentPos = orientation === 'horizontal' ? moveEvent.clientX : moveEvent.clientY;
      const deltaPercent = ((currentPos - startPos) / totalSizePixels) * 100;
      const newSizes = [...initialSizes];
      newSizes[index] = initialSizes[index] + deltaPercent;
      if (newSizes[index + 1] !== undefined) {
        newSizes[index + 1] = initialSizes[index + 1] - deltaPercent;
      }
      if (newSizes[index] > 10 && (newSizes[index + 1] === undefined || newSizes[index + 1] > 10)) {
        setDimensionsConfig(prev => ({ ...prev, [layout]: { ...prev[layout], [context]: newSizes } }));
      }
    };
    const handleMouseUp = () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); document.body.style.cursor = ''; };
    document.addEventListener('mousemove', handleMouseMove); document.addEventListener('mouseup', handleMouseUp);
  };

  const renderPanel = (slotIndex: number, forcedView?: ViewState) => {
    const currentSlots = configurations[layout] || [];
    const slot = currentSlots.find(s => s.id === slotIndex);
    const viewToShow = forcedView || slot?.view;
    const slotDark = getSlotDarkMode(viewToShow);
    const meta = viewToShow ? MODULE_META[viewToShow] : null;
    const label = meta?.label || (viewToShow ? 'Módulo' : 'Vacío');
    const isOver = draggedOverSlotId === slotIndex;
    const isMaximized = maximizedSlotId === slotIndex;
    const isReceptive = draggingSidebarView !== null;

    return (
      <div className={`w-full h-full rounded-xl shadow-sm border flex flex-col overflow-hidden relative transition-all duration-200 ${slotDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'} ${isOver ? 'ring-4 ring-blue-500 ring-inset border-blue-500 bg-blue-50/10' : (isReceptive ? 'border-blue-400/50 bg-blue-500/5' : '')}`} onDragOver={(e) => handleDragOverSlot(e, slotIndex)} onDragLeave={() => setDraggedOverSlotId(null)} onDrop={(e) => handleDropOnSlot(e, slotIndex)}>
        <div className={`h-8 min-h-[32px] border-b flex items-center justify-between px-3 select-none ${slotDark ? 'bg-[#1e2024] border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-center space-x-2 truncate">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${viewToShow ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            
            {/* Selector de Módulo Rápido (Incluso en Pop-out) */}
            <div className="relative group/modsel">
              <button className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-wide truncate ${slotDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'} transition-colors`}>
                {label}
                <ChevronDown size={10} />
              </button>
              
              <div className="absolute left-0 top-full mt-0 pt-1 hidden group-hover/modsel:block z-[60] animate-in fade-in slide-in-from-top-1 duration-200">
                <div className={`p-1 rounded-xl border shadow-xl min-w-[160px] ${slotDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                  {Object.entries(MODULE_META).filter(([vId]) => {
                    if (['PROFILE', 'LOGIN', 'DASHBOARD_USER'].includes(vId)) return false;
                    if (userDisabledModules.includes(vId)) return false;
                    if (vId === ViewState.ADMIN_PANEL) {
                      const email = (user?.email || '').toLowerCase();
                      return email === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline' || userProfile?.role === 'ADMIN';
                    }
                    return true;
                  }).map(([viewId, m]) => (
                    <button 
                      key={viewId}
                      onClick={() => updateSlot(slotIndex, viewId as ViewState)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-[9px] font-black uppercase tracking-tighter transition-all ${viewToShow === viewId ? 'bg-blue-600 text-white shadow-sm' : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-600'}`}
                    >
                      <m.icon size={12} />
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isPopOut && layout === 'single' && onTogglePin && (
              <button 
                onClick={onTogglePin}
                className={`p-1 rounded-md transition-all ${isPinned ? 'text-blue-600 bg-blue-600/10 scale-110 shadow-sm' : 'text-gray-400 hover:text-blue-500'}`}
                title={isPinned ? "Desanclar ventana" : "Anclar ventana (Recordar posición)"}
              >
                <Pin size={14} className={isPinned ? "fill-current" : ""} />
              </button>
            )}
            
            {viewToShow && (
               <button onClick={() => setMaximizedSlotId(isMaximized ? null : slotIndex)} className="text-gray-400 hover:text-blue-500 transition-colors">{isMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</button>
            )}
            {viewToShow && (
              <button onClick={() => { if(isMaximized) setMaximizedSlotId(null); updateSlot(slotIndex, null); }} className="text-gray-400 hover:text-red-500 transition-colors"><X size={14} /></button>
            )}
          </div>
        </div>
        <div className="flex-1 relative overflow-hidden">
          {viewToShow ? (
            <div className="absolute inset-0 overflow-auto">
              {renderModule(viewToShow)}
            </div>
          ) : (
            <div className={`absolute inset-0 flex flex-col items-center justify-center m-2 rounded-lg border-2 border-dashed ${isDarkMode ? 'text-gray-600 bg-[#1e2024]/50 border-gray-700' : 'text-gray-300 bg-gray-50/50 border-gray-200'} ${isOver ? 'border-blue-500 bg-blue-50/10' : ''}`}>
              {isOver ? (
                 <Plus size={32} className="mb-2 text-blue-500 animate-bounce" />
              ) : (
                 <Square size={32} className={`mb-2 transition-opacity ${isReceptive ? 'opacity-100 text-blue-400 animate-pulse' : 'opacity-20'}`} />
              )}
              <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${isOver || isReceptive ? 'text-blue-500' : ''}`}>
                {isOver ? 'Soltar módulo aquí' : isReceptive ? 'Disponible para soltar' : 'Arrastra un módulo'}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLayoutContent = () => {
    // BYPASS MAESTRO RADICAL Y DEFINITIVO: Sin flickering, sin re-validaciones.
    const userEmail = (user?.email || "").toLowerCase();
    const isMasterEmail = userEmail === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
    const isMaster = isMasterEmail || (isMasterLatched.current && isMasterEmail) || isMasterUser || (userProfile && userProfile.role === 'ADMIN');
    
    if (isMaster) return renderLayoutFullView();

    const isUnvalidated = userProfile && !userProfile.isVerified;
    const forceSingleView = (isSmallScreen || isUnvalidated);
    
    if (forceSingleView) {
      // Si no es el Maestro y no está validado, forzar Dashboard (excepto en Perfil para que pueda completarlo)
      const viewToForce = isUnvalidated 
        ? (activeView === ViewState.PROFILE ? ViewState.PROFILE : ViewState.DASHBOARD_USER)
        : activeView;
      return <div className="w-full h-full p-2">{renderPanel(0, viewToForce)}</div>;
    }

    return renderLayoutFullView();
  };

  const renderLayoutFullView = () => {
    if (maximizedSlotId !== null) return <div className="w-full h-full p-2 animate-in zoom-in-95 duration-200">{renderPanel(maximizedSlotId)}</div>;
    const activeDimensions = dimensionsConfig[layout];
    const { main } = activeDimensions;
    if (layout === 'single') return <div className="w-full h-full p-2">{renderPanel(0)}</div>;
    if (['split-vertical', 'split-three', 'split-four', 'split-three-focus'].includes(layout)) return (<div className="flex w-full h-full p-2">{main.map((width, i) => (<React.Fragment key={i}><div style={{ width: `${width}%` }} className="h-full">{renderPanel(i)}</div>{i < main.length - 1 && <ResizeHandle orientation="horizontal" onMouseDown={(e) => handleResizeStart(e, i, 'main', 'horizontal')} />} </React.Fragment>))}</div>);
    if (layout === 'split-horizontal') return (<div className="flex flex-col w-full h-full p-2">{main.map((height, i) => (<React.Fragment key={i}><div style={{ height: `${height}%` }} className="w-full">{renderPanel(i)}</div>{i < main.length - 1 && <ResizeHandle orientation="vertical" onMouseDown={(e) => handleResizeStart(e, i, 'main', 'vertical')} />}</React.Fragment>))}</div>);
    if (layout === 'mixed') return (<div className="flex w-full h-full p-2"><div style={{ width: `${main[0]}%` }} className="h-full">{renderPanel(0)}</div><ResizeHandle orientation="horizontal" onMouseDown={(e) => handleResizeStart(e, 0, 'main', 'horizontal')} /><div style={{ width: `${main[1]}%` }} className="h-full flex flex-col"><div style={{ height: `${activeDimensions.subRight?.[0] || 50}%` }} className="w-full pb-1">{renderPanel(1)}</div><ResizeHandle orientation="vertical" onMouseDown={(e) => handleResizeStart(e, 0, 'subRight', 'vertical')} /><div style={{ height: `${activeDimensions.subRight?.[1] || 50}%` }} className="w-full pt-1">{renderPanel(2)}</div></div></div>);
    if (layout === 'mixed-mirror') return (<div className="flex w-full h-full p-2"><div style={{ width: `${main[0]}%` }} className="h-full flex flex-col"><div style={{ height: `${activeDimensions.subLeft?.[0] || 50}%` }} className="w-full pb-1">{renderPanel(0)}</div><ResizeHandle orientation="vertical" onMouseDown={(e) => handleResizeStart(e, 0, 'subLeft', 'vertical')} /><div style={{ height: `${activeDimensions.subLeft?.[1] || 50}%` }} className="w-full pt-1">{renderPanel(2)}</div></div><ResizeHandle orientation="horizontal" onMouseDown={(e) => handleResizeStart(e, 0, 'main', 'horizontal')} /><div style={{ width: `${main[1]}%` }} className="h-full">{renderPanel(1)}</div></div>);
    if (layout === 'grid-2x2') return (<div className="flex w-full h-full p-2"><div style={{ width: `${main[0]}%` }} className="h-full flex flex-col"><div style={{ height: `${activeDimensions.subLeft?.[0] || 50}%` }} className="w-full pb-1">{renderPanel(0)}</div><ResizeHandle orientation="vertical" onMouseDown={(e) => handleResizeStart(e, 0, 'subLeft', 'vertical')} /><div style={{ height: `${activeDimensions.subLeft?.[1] || 50}%` }} className="w-full pt-1">{renderPanel(2)}</div></div><ResizeHandle orientation="horizontal" onMouseDown={(e) => handleResizeStart(e, 0, 'main', 'horizontal')} /><div style={{ width: `${main[1]}%` }} className="h-full flex flex-col"><div style={{ height: `${activeDimensions.subRight?.[0] || 50}%` }} className="w-full pb-1">{renderPanel(1)}</div><ResizeHandle orientation="vertical" onMouseDown={(e) => handleResizeStart(e, 0, 'subRight', 'vertical')} /><div style={{ height: `${activeDimensions.subRight?.[1] || 50}%` }} className="w-full pt-1">{renderPanel(3)}</div></div></div>);
    return null;
  };

  const mobileNavItems = useMemo(() => {
    // La fuente de los módulos móviles siempre es mobileViewsLibre para respetar
    // directamente la configuración explícita de visibilidad gestionada por el usuario en ajustes.
    const sourceItems = mobileViewsLibre;

    const filteredSourceItems = isUnvalidated
      ? sourceItems.filter(view => view === ViewState.PROFILE || view === ViewState.DASHBOARD_USER)
      : sourceItems;

    // Filtramos para asegurar que el módulo exista y esté marcado como visible para móvil
    // Nota: mobileViewsLibre actúa como la "Matriz de Visibilidad" para la plataforma móvil
    const items = filteredSourceItems
      .filter(view => MODULE_META[view] && (isUnvalidated || mobileViewsLibre.includes(view)))
      .filter(view => !userDisabledModules.includes(view))
      .map(view => ({
        id: view,
        label: MODULE_META[view].label,
        icon: MODULE_META[view].icon
      }));
    
    return items;
  }, [appProfile, mobileViewsLibre, sidebarSections, isMasterUser, isUnvalidated, userDisabledModules]);
  
  return (
    <div className={`flex h-screen overflow-hidden font-sans ${sidebarPos === 'right' ? 'flex-row-reverse' : 'flex-row'} ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-100'}`}>
      <div className={`hidden md:flex ${isSidebarOpen ? 'w-64' : 'w-16'} border-r flex-col flex-shrink-0 shadow-xl z-50 transition-all duration-300 ${appTheme === 'day' ? 'bg-white border-gray-200 text-slate-700' : 'bg-[#2a2d34] border-gray-700 text-gray-300'}`}>
        <div className={`p-4 border-b flex ${isSidebarOpen ? 'justify-between' : 'justify-center'} items-center ${appTheme === 'day' ? 'bg-gray-50/50 border-gray-100' : 'bg-[#1e2024]/50 border-gray-700'}`}>
          {isSidebarOpen && (
            <div className="hidden md:flex items-center gap-3">
              <AppLogo size={42} />
              <div className="flex flex-col items-start translate-x-[-4px]">
                  <h2 className={`font-bold text-xl tracking-tight leading-none font-['Outfit'] ${appTheme === 'day' ? 'text-slate-600' : 'text-gray-100'}`}>MODULAR</h2>
                  <span className="text-[9px] text-gray-400 font-bold tracking-wide whitespace-nowrap font-['Outfit'] mt-1">RADIOLOGY DESKTOP</span>
              </div>
            </div>
          )}
          <div className={`flex ${isSidebarOpen ? 'items-center space-x-1' : 'flex-col space-y-3'}`}>
            <button onClick={() => { setSidebarPos(prev => prev === 'left' ? 'right' : 'left'); }} className="text-gray-400 hover:text-orange-500 transition-colors" title="Mover Barra Lateral">
              {sidebarPos === 'left' ? <MoveRight size={18} /> : <MoveLeft size={18} />}
            </button>
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-gray-400 hover:text-orange-500 transition-colors" title={isSidebarOpen ? "Ocultar Menú" : "Mostrar Menú"}>
              {isSidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
          </div>
        </div>
        
        {isSidebarOpen && (
          <div className={`p-4 border-b space-y-3 pb-2 ${appTheme === 'day' ? 'bg-white border-gray-200' : 'bg-[#2a2d34] border-gray-700'}`}>
            <div className="hidden md:block mb-1">
              <div className="flex items-center space-x-3 mb-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-gray-500 overflow-hidden shrink-0 ${isMasterUser ? 'ring-2 ring-amber-400 ring-offset-2' : 'bg-gray-100'}`}>
                  {user?.photoURL ? <img src={user.photoURL} alt="Avatar" className="w-full h-full object-cover" /> : <UserIcon size={20} />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className={`text-sm font-bold truncate leading-tight ${appTheme === 'day' ? 'text-gray-800' : 'text-gray-100'}`}>{userProfile?.displayName || user?.displayName || 'Doctor'}</p>
                    {userProfile?.role === 'RADIOLOGIST' && userProfile?.experienceYears === 'más de 30 años' && <Crown size={12} className="text-amber-500 shrink-0 mb-0.5" />}
                  </div>
                  <p className={`text-[9px] font-black uppercase tracking-widest text-blue-500`}>{userProfile?.role || 'Médico Radiólogo'}</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-1">
              <div className="relative w-full" ref={statusDropdownRef}>
                <button onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)} className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between text-xs font-bold transition-colors group ${appTheme === 'day' ? 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100' : 'bg-[#1e2024] border-gray-700 text-gray-300 hover:bg-[#32363d]'}`}>
                   <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[userProfile?.status || 'AVAILABLE'].color}`}></div>
                      <span>{STATUS_CONFIG[userProfile?.status || 'AVAILABLE'].label}</span>
                   </div>
                   <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* ... existing isStatusDropdownOpen block ... */}
                {isStatusDropdownOpen && (
                  <div className={`absolute top-full left-0 right-0 mt-1.5 rounded-xl shadow-2xl border overflow-hidden z-[100] animate-in fade-in zoom-in-95 duration-200 ${appTheme === 'day' ? 'bg-white border-gray-100' : 'bg-[#1e2024] border-gray-700'}`}>
                     {(Object.keys(STATUS_CONFIG) as CollaborationStatus[]).map((key) => {
                       const config = STATUS_CONFIG[key];
                       const isActive = (userProfile?.status || 'AVAILABLE') === key;
                       return (
                         <button 
                           key={key} 
                           onClick={() => handleStatusChange(key)}
                           className={`w-full px-4 py-3 flex items-center gap-3 transition-all text-left ${appTheme === 'day' ? 'hover:bg-blue-50/50' : 'hover:bg-indigo-900/10'}`}
                         >
                           <div className={`w-2.5 h-2.5 rounded-full ${config.color} ${isActive ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}></div>
                           <span className={`text-[11px] font-black uppercase tracking-tight flex-1 ${appTheme === 'day' ? (isActive ? 'text-blue-600' : 'text-gray-700') : (isActive ? 'text-blue-400' : 'text-gray-200')}`}>
                              {config.label}
                           </span>
                           {isActive && <Check size={14} className="text-blue-500" />}
                         </button>
                       );
                     })}
                  </div>
                )}
              </div>
              {!hideProfileSystem && (
                <div className="flex items-center justify-center gap-6 px-1 py-1">
                  <button onClick={() => applyPresetProfile('Basico')} title="Básico" className={`w-4 h-4 rounded transition-all duration-300 border-2 bg-blue-600 ${appProfile === 'Basico' ? 'scale-125 border-white dark:border-white shadow-lg' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'}`} />
                  <button onClick={() => applyPresetProfile('Medio')} title="Medio" className={`w-4 h-4 rounded transition-all duration-300 border-2 bg-blue-300 ${appProfile === 'Medio' ? 'scale-125 border-white dark:border-white shadow-lg' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'}`} />
                  <button onClick={() => applyPresetProfile('Avanzado')} title="Avanzado" className={`w-4 h-4 rounded transition-all duration-300 border-2 bg-indigo-600 ${appProfile === 'Avanzado' ? 'scale-125 border-white dark:border-white shadow-lg' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'}`} />
                  <button onClick={() => applyPresetProfile('Libre')} title="Personalizado" className={`w-4 h-4 rounded transition-all duration-300 border-2 bg-purple-500 ${appProfile === 'Libre' ? 'scale-125 border-white dark:border-white shadow-lg' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-110'}`} />
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex flex-1 overflow-y-auto py-2 scrollbar-none flex-col">
          {sidebarSections.map((section, sIndex) => {
            return (
              <div key={section.id} className="mb-4 relative" onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleSidebarDropOnEmptySection(e, sIndex)}>
                {sIndex > 0 && <div className={`h-px mx-4 my-2 ${appTheme === 'day' ? 'bg-gray-100' : 'bg-gray-700'}`} />}
                <div className="space-y-1 pt-1 min-h-[10px]">
                  {section.items.map((viewId, itemIndex) => {
                    const meta = MODULE_META[viewId];
                    if (!meta) return null;
                    if (userDisabledModules.includes(viewId)) return null;
                    if (isUnvalidated && viewId !== ViewState.PROFILE && viewId !== ViewState.DASHBOARD_USER) return null;
                    const isVisibleInLayout = isViewInAnySlot(viewId);
                    const isCommunity = viewId === ViewState.COMMUNITY;

                    return (
                      <div
                        key={viewId}
                        draggable={true}
                        onDragStart={(e) => handleSidebarDragStart(e, sIndex, itemIndex, viewId)}
                        onDragEnd={handleSidebarDragEnd}
                        onDragOver={(e) => handleSidebarDragOverItem(e, sIndex, itemIndex)}
                        onDrop={(e) => handleSidebarDropOnItem(e, sIndex, itemIndex)}
                        className={`group flex items-center px-4 py-3 rounded-xl transition-all border border-transparent relative hover:text-orange-500 ${isSidebarOpen ? 'mx-3' : 'mx-1 px-0 justify-center'} ${dragOverSidebarTarget?.sectionIndex === sIndex && dragOverSidebarTarget?.itemIndex === itemIndex ? 'border-orange-500 bg-orange-50/10' : ''} ${isVisibleInLayout ? (appTheme === 'day' ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-indigo-900/20 text-blue-300 font-bold') : (appTheme === 'day' ? 'text-gray-500 hover:bg-orange-50' : 'text-gray-400 hover:bg-[#32363d]')} cursor-grab active:cursor-grabbing`}
                        onClick={() => onNavigate(viewId)}
                      >
                        <div className={`${isSidebarOpen ? 'mr-3' : ''} relative transition-transform group-hover:scale-110 duration-200`}>
                          <meta.icon size={22} className="group-hover:text-orange-500 transition-colors" />
                          {isCommunity && hasUnreadCommunity && (
                            <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 ${appTheme === 'day' ? 'border-white' : 'border-[#2a2d34]'} animate-pulse`} />
                          )}
                        </div>
                        {isSidebarOpen && <span className={`text-sm font-medium truncate ${isCommunity && hasUnreadCommunity ? 'text-red-500 font-bold' : ''}`}>{meta.label}</span>}
                        {isSidebarOpen && <GripVertical className="ml-auto text-blue-300 opacity-0 group-hover:opacity-100 hidden md:block transition-opacity" size={16} />}

                        {!isSidebarOpen && (
                          <div className={`absolute ${sidebarPos === 'left' ? 'left-full ml-3' : 'right-full mr-3'} px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-[100] transition-all duration-200 shadow-xl ring-1 ring-white/10`}>
                            {meta.label}
                            <div className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 ${sidebarPos === 'left' ? '-left-1' : '-right-1'}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className={`mt-auto pt-4 px-3 pb-4 border-t ${appTheme === 'day' ? 'border-gray-100' : 'border-gray-700'} ${!isSidebarOpen ? 'flex flex-col items-center' : ''}`}>
          <button 
            onClick={() => onNavigate(ViewState.PROFILE)} 
            className={`w-full flex items-center px-4 py-3 mb-1 rounded-xl transition-all group hover:text-blue-500 ${!isSidebarOpen ? 'px-0 justify-center' : ''} ${isViewInAnySlot(ViewState.PROFILE) || activeView === ViewState.PROFILE ? (appTheme === 'day' ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-indigo-900/20 text-blue-300 font-bold') : (appTheme === 'day' ? 'text-gray-500 hover:bg-blue-50' : 'text-gray-400 hover:bg-blue-500/10')}`}
          >
            <div className={`group-hover:scale-110 transition-transform ${isSidebarOpen ? 'mr-3' : ''}`}>
              <UserIcon size={20} className="group-hover:text-blue-500 transition-colors" />
            </div>
            {isSidebarOpen && <span className="text-sm font-medium">Perfil</span>}
            {!isSidebarOpen && (
              <div className={`absolute ${sidebarPos === 'left' ? 'left-full ml-3' : 'right-full mr-3'} px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-[100] transition-all duration-200 shadow-xl ring-1 ring-white/10`}>
                 Perfil
                 <div className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 ${sidebarPos === 'left' ? '-left-1' : '-right-1'}`} />
              </div>
            )}
          </button>
          <button onClick={() => setIsSettingsOpen(true)} className={`w-full flex items-center px-4 py-3 mb-1 rounded-xl transition-all group hover:text-orange-500 ${!isSidebarOpen ? 'px-0 justify-center' : ''} ${appTheme === 'day' ? 'text-gray-500 hover:bg-orange-50' : 'text-gray-400 hover:bg-orange-500/10'}`}>
            <div className={`group-hover:rotate-45 transition-transform ${isSidebarOpen ? 'mr-3' : ''}`}><Settings size={20} /></div>
            {isSidebarOpen && <span className="text-sm font-medium">Ajustes</span>}
            {!isSidebarOpen && (
              <div className={`absolute ${sidebarPos === 'left' ? 'left-full ml-3' : 'right-full mr-3'} px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-[100] transition-all duration-200 shadow-xl ring-1 ring-white/10`}>
                 Ajustes
                 <div className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 ${sidebarPos === 'left' ? '-left-1' : '-right-1'}`} />
              </div>
            )}
          </button>
          <button onClick={onLogout} className={`w-full flex items-center px-4 py-3 rounded-xl transition-all group hover:text-orange-500 ${!isSidebarOpen ? 'px-0 justify-center' : ''} ${appTheme === 'day' ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-900/10'}`}>
            <div className={`group-hover:translate-x-1 transition-transform ${isSidebarOpen ? 'mr-3' : ''}`}><LogOut size={20} /></div>
            {isSidebarOpen && <span className="text-sm font-bold">Salir</span>}
            {!isSidebarOpen && (
              <div className={`absolute ${sidebarPos === 'left' ? 'left-full ml-3' : 'right-full mr-3'} px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-[100] transition-all duration-200 shadow-xl ring-1 ring-white/10`}>
                 Salir
                 <div className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 ${sidebarPos === 'left' ? '-left-1' : '-right-1'}`} />
              </div>
            )}
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col min-w-0">
        <div className={`hidden md:flex border-b px-6 py-2 justify-between items-center shadow-sm z-40 ${appTheme === 'day' ? 'bg-white border-gray-200' : 'bg-[#2a2d34] border-gray-700'}`}>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setIsDesktopLayoutOpen(!isDesktopLayoutOpen)} 
              className={`p-2 rounded-lg transition-all border flex items-center justify-center ${isDesktopLayoutOpen ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-100' : (appTheme === 'day' ? 'text-gray-500 bg-white border-gray-200 hover:bg-gray-50' : 'text-gray-400 bg-[#1e2024] border-gray-700 hover:bg-[#32363d]')}`} 
              title="Layout"
            >
              <AppLogo size={20} className={isDesktopLayoutOpen ? "brightness-0 invert" : ""} />
            </button>
            {isDesktopLayoutOpen && (
              <div className="flex items-center space-x-2 border-l border-gray-200 pl-4 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="flex items-center gap-1.5 px-1 min-w-max">
                  {visibleLayouts.map((lType) => {
                    const meta = LAYOUT_META[lType];
                    const Icon = meta.icon;
                    return (
                      <button
                        key={lType}
                        onClick={() => changeLayout(lType)}
                        className={`p-1.5 rounded-lg hover:bg-blue-50 transition-all ${layout === lType ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
                        title={meta.label}
                      >
                        <Icon />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleToggleAgent}
              disabled={isAgentProcessing || isRemoteLocked}
              className={`relative px-4 py-2 rounded-xl border flex items-center space-x-2 transition-all shadow-sm group ${isAgentRecording ? 'bg-red-500 border-red-600 text-white shadow-[0_0_20px_rgba(239,68,68,0.5)]' : (isAgentProcessing || isRemoteLocked ? 'bg-gray-100 text-gray-400' : (appTheme === 'day' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-indigo-900/20 border-indigo-500/30 text-indigo-400'))}`}
              title={isRemoteLocked ? "Micrófono bloqueado por otra pestaña" : "AGENTE: Escucha Activa"}
            >
               <div className="relative">
                  {isAgentRecording && (
                    <div className="absolute -inset-2 rounded-full border-2 border-white/50 animate-ping z-0" />
                  )}
                  <div className="relative z-10">
                    {isAgentProcessing ? <RefreshCw size={18} className="animate-spin" /> : isAgentRecording ? <Square size={18} fill="currentColor" /> : isRemoteLocked ? <Activity size={18} /> : <Zap size={18} className="group-hover:rotate-12 transition-transform" />}
                  </div>
                </div>
                {(isAgentProcessing || isAgentRecording || isRemoteLocked) && (
                  <span className="text-[11px] font-black uppercase tracking-widest">
                    {isAgentProcessing ? 'Procesando' : isAgentRecording ? 'Escuchando' : 'BLOQUEADO'}
                  </span>
                )}
             </button>
             <button
                onClick={() => {
                  const order: ('day' | 'mixed' | 'dark')[] = ['day', 'mixed', 'dark'];
                  const next = order[(order.indexOf(appTheme) + 1) % order.length];
                  handleGlobalThemeChange(next);
                }}
                className={`p-2 rounded-xl border mr-2 transition-all ${appTheme === 'day' ? 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200' : appTheme === 'mixed' ? 'bg-[#1e2024] border-gray-700 text-indigo-300 hover:bg-[#2a2d34]' : 'bg-slate-800 border-slate-700 text-yellow-400 hover:bg-slate-700'}`}
                title={appTheme === 'day' ? 'Día' : appTheme === 'mixed' ? 'Mixto' : 'Noche'}
             >
                {appTheme === 'day' ? <Sun size={18} /> : appTheme === 'mixed' ? <SunMoon size={18} /> : <Moon size={18} />}
             </button>
             <div className={`flex items-center rounded-xl p-1 border transition-colors ${appTheme === 'day' ? 'bg-gray-100 border-gray-200' : 'bg-gray-800 border-gray-700'}`}>
               <button 
                 onClick={toggleTextMirroring}
                 className={`p-1.5 rounded-lg transition-all ${isTextMirroringActive ? 'bg-blue-500 text-white shadow-md' : (appTheme === 'day' ? 'text-gray-400 hover:bg-gray-200' : 'text-gray-400 hover:bg-gray-700')}`}
                 title={isTextMirroringActive ? "Modo Espejo Activo (Escuchando Móvil)" : "Activar Modo Espejo (Recibir Texto de Móvil)"}
               >
                 <Cast size={18} className={isTextMirroringActive ? 'animate-pulse' : ''} />
               </button>
               <div className={`w-px h-4 mx-1 ${appTheme === 'day' ? 'bg-gray-300' : 'bg-gray-600'}`}></div>
               <button onClick={() => performCloudBackup(true)} disabled={isBackingUpCloud} className={`p-1.5 rounded-lg transition-all ${backupSuccess ? 'bg-emerald-500 text-white shadow-md' : (appTheme === 'day' ? 'text-gray-400 hover:bg-gray-200' : 'text-gray-400 hover:bg-gray-700')}`} title="Respaldo Rápido en Nube"> {isBackingUpCloud ? <RefreshCw size={18} className="animate-spin" /> : backupSuccess ? <Check size={18} /> : <UploadCloud size={18} />} </button>
               <div className={`w-px h-4 mx-1 ${appTheme === 'day' ? 'bg-gray-300' : 'bg-gray-600'}`}></div>
               <button onClick={togglePersistence} className={`p-1.5 rounded-lg transition-all ${windowPersistenceEnabled ? 'bg-blue-600 text-white shadow-md' : (appTheme === 'day' ? 'text-gray-400 hover:bg-gray-200' : 'text-gray-400 hover:bg-gray-700')}`} title="Persistencia Maestra">{windowPersistenceEnabled ? <Pin size={18} /> : <PinOff size={18} />}</button>
               <div className={`w-px h-4 mx-1 ${appTheme === 'day' ? 'bg-gray-300' : 'bg-gray-600'}`}></div>
               <button onClick={handleOpenMobileView} className={`p-1.5 rounded-lg transition-colors ${appTheme === 'day' ? 'text-gray-400 hover:text-blue-600 hover:bg-gray-50' : 'text-gray-400 hover:text-blue-400 hover:bg-gray-800'}`} title="Restaurar Ventanas Móviles (Smartphone Mode)"><Smartphone size={22} /></button>
               <button onClick={handleSimplePopOut} className={`p-1.5 rounded-lg transition-colors ${appTheme === 'day' ? 'text-gray-400 hover:text-indigo-600 hover:bg-gray-50' : 'text-gray-400 hover:text-indigo-400 hover:bg-gray-800'}`} title="Abrir Nueva Ventana (Forzado)"><ExternalLink size={20} /></button>
             </div>
           </div>
        </div>
        
        {/* Aviso de Validación de Perfil */}
        {userProfile && (!userProfile.isVerified || isProfileIncomplete) && activeView !== ViewState.PROFILE && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className={`w-full overflow-hidden border-b transition-colors ${appTheme === 'day' ? 'bg-amber-50 border-amber-100' : 'bg-amber-900/10 border-amber-500/20'}`}
          >
            <div className="px-6 py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${appTheme === 'day' ? 'bg-amber-100 text-amber-600' : 'bg-amber-500/20 text-amber-400'}`}>
                  <ShieldAlert size={18} />
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-black uppercase tracking-tight ${appTheme === 'day' ? 'text-amber-800' : 'text-amber-200'}`}>
                    Perfil Incompleto o No Validado
                  </span>
                  <span className={`text-[10px] font-medium ${appTheme === 'day' ? 'text-amber-600' : 'text-amber-400/80'}`}>
                    Completa tu perfil para acceder a todas las funciones master de MODULAR.
                  </span>
                </div>
              </div>
              <motion.button 
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                onClick={() => onNavigate(ViewState.PROFILE)}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${appTheme === 'day' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-amber-600 text-white hover:bg-amber-500'}`}
              >
                Valida tu usuario
              </motion.button>
            </div>
          </motion.div>
        )}

        <div className={`flex-1 overflow-hidden relative ${(isSmallScreen || isPopOut) ? 'pb-[84px]' : ''}`}>{renderLayoutContent()}</div>
        
        <div className={`${isPopOut ? 'block' : 'md:hidden'} z-50 fixed bottom-0 left-0 right-0 h-[84px] w-full`}>
           <BottomNav 
             currentView={activeView} 
             onNavigate={onNavigate} 
             items={mobileNavItems} 
             isDarkMode={isDarkMode} 
             isAgentRecording={isAgentRecording} 
             isAgentProcessing={isAgentProcessing} 
             onToggleAgent={handleToggleAgent}
             notifications={{ [ViewState.COMMUNITY]: hasUnreadCommunity }}
           />
        </div>
      </div>
      
      {/* Settings Drawer (Panel Lateral) */}
      <AnimatePresence>
        {isSettingsOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSettingsOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
            />
            
            {/* Drawer Content */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-full max-w-md z-[101] shadow-2xl flex flex-col ${isDarkMode ? 'bg-[#1e2024] text-gray-200' : 'bg-white text-gray-800'}`}
            >
              {/* Header */}
              <div className={`p-6 border-b flex flex-col gap-4 ${isDarkMode ? 'border-gray-800 bg-[#2a2d34]' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-600 rounded-xl text-white shadow-lg shadow-purple-600/20">
                      <Settings size={20} />
                    </div>
                    <div>
                      <h2 className="text-sm font-black uppercase tracking-widest">Ajustes del Sistema</h2>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Configuración Maestra</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsSettingsOpen(false)}
                    className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Tabs Selector */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-2 no-scrollbar px-0.5">
                  {[
                    { id: 'interface', label: 'Interfaz', icon: Palette },
                    { id: 'mobile', label: 'Móvil', icon: Smartphone },
                    { id: 'auto', label: 'AUTO', icon: Zap },
                    { id: 'sync', label: 'Sincro', icon: Database },
                    { id: 'system', label: 'Sistema', icon: Wrench },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSettingsTab(tab.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border shrink-0 ${
                        activeSettingsTab === tab.id 
                          ? 'bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-600/20' 
                          : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')
                      }`}
                    >
                      <tab.icon size={13} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-8">
                
                {/* Categoría: Interfaz y Estética */}
                {activeSettingsTab === 'interface' && (
                  <section className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-2 text-blue-500">
                      <Palette size={16} />
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Interfaz y Estética</h3>
                    </div>
                    
                    <div className="space-y-6 pl-2">
                      {/* Tema Visual */}
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tema Visual</label>
                        <div className={`flex p-1.5 rounded-2xl border ${isDarkMode ? 'bg-black/20 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                          {(['day', 'mixed', 'dark'] as AppTheme[]).map(t => (
                            <button key={t} onClick={() => handleGlobalThemeChange(t)} className={`flex-1 py-2 text-[10px] font-black uppercase rounded-xl transition-all ${appTheme === t ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400'}`}>
                              {t === 'day' ? 'Día' : t === 'mixed' ? 'Mixto' : 'Noche'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Notificaciones Globales */}
                      <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Notificaciones</label>
                        <button 
                          onClick={() => {
                            const next = !notificationsEnabled;
                            setNotificationsEnabled(next);
                            localStorage.setItem('modular_enable_notifications', next.toString());
                            window.dispatchEvent(new Event('app_settings_changed')); 
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${notificationsEnabled ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}
                        >
                          <div className="flex flex-col items-start text-left">
                            <span className={`text-[10px] font-black uppercase ${notificationsEnabled ? (isDarkMode ? 'text-blue-400' : 'text-blue-950') : 'text-gray-400'}`}>Burbujas de Mensaje</span>
                            <span className="text-[9px] text-gray-400 font-bold">Resumen flotante de nuevos mensajes</span>
                          </div>
                          {notificationsEnabled ? <ToggleRight className="text-indigo-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                        </button>
                      </div>

                      {/* Visibilidad de Layouts */}
                      <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Visibilidad de Layouts</label>
                        <div className="grid grid-cols-5 gap-2">
                          {(Object.keys(LAYOUT_META) as ModularLayoutType[]).map((lType) => {
                            const meta = LAYOUT_META[lType];
                            const Icon = meta.icon;
                            const isVisible = visibleLayouts.includes(lType);
                            return (
                              <button
                                key={lType}
                                onClick={() => toggleLayoutVisibility(lType)}
                                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all ${
                                  isVisible 
                                    ? 'bg-blue-50 border-blue-200 text-blue-600' 
                                    : 'bg-transparent border-gray-100 dark:border-gray-800 text-gray-400 grayscale opacity-50'
                                }`}
                                title={meta.label}
                              >
                                <Icon />
                                <span className="text-[8px] font-black uppercase truncate w-full text-center">{meta.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {!hideProfileSystem && (
                        <>
                          {/* Perfil de Aplicación */}
                          <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                              Perfil de Aplicación
                              <button onClick={() => toggleHelp('profile')} className="ml-auto text-blue-500 hover:text-blue-600"><HelpCircle size={12} /></button>
                            </label>
                            {visibleHelp.profile && <div className="p-3 rounded-xl bg-gray-100 dark:bg-blue-900/20 border border-gray-300 dark:border-blue-900/30 text-[11px] text-blue-950 dark:text-blue-200 font-bold leading-snug"> Determina qué módulos son visibles por defecto según tu nivel de complejidad. </div>}
                            <div className={`grid grid-cols-2 gap-2 p-1.5 rounded-2xl border ${isDarkMode ? 'bg-black/20 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                              {(['Basico', 'Medio', 'Avanzado', 'Libre'] as AppProfile[]).map(p => (
                                <button key={p} onClick={() => applyPresetProfile(p)} className={`py-2 text-[10px] font-black uppercase rounded-xl transition-all ${appProfile === p ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}>{p}</button>
                              ))}
                            </div>
                            
                            {appProfile === 'Libre' && (
                              <button 
                                onClick={handleSaveLibreConfig}
                                className="w-full mt-4 py-3 bg-purple-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:bg-purple-700 flex items-center justify-center gap-2 animate-in fade-in zoom-in-95 duration-300"
                              >
                                <Save size={14} />
                                Guardar Configuración Libre
                              </button>
                            )}
                          </div>

                          {/* Matriz de Visibilidad - Solo visible en modo Libre */}
                          {appProfile === 'Libre' && (
                            <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-top-2 duration-500">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                                Matriz de Visibilidad
                              </label>
                                
                                <div className="space-y-2">
                                  <div className="grid grid-cols-[1fr_40px] gap-2 px-2 mb-1">
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Módulo</span>
                                    <div className="flex justify-center"><Layout size={12} className="text-gray-400" title="Escritorio (Web)" /></div>
                                  </div>
                                  
                                  {Object.entries(MODULE_META).map(([viewId, meta]) => {
                                    const vId = viewId as ViewState;
                                    const isVisibleWeb = sidebarSections.some(s => s.items.includes(vId));
                                    
                                    return (
                                      <div key={vId} className={`grid grid-cols-[1fr_40px] gap-2 items-center p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-black/10 border-gray-800' : 'bg-white border-gray-100'}`}>
                                        <div className="flex items-center gap-2">
                                          <meta.icon size={14} className="text-blue-500" />
                                          <span className="text-[10px] font-bold truncate">{meta.label}</span>
                                        </div>
                                        
                                        <button 
                                          onClick={() => toggleModuleVisibility(vId, 'web')}
                                          className={`flex justify-center p-1 rounded-lg transition-colors ${isVisibleWeb ? 'text-blue-500 bg-blue-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                                        >
                                          {isVisibleWeb ? <CheckCircle size={16} /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-700" />}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </section>
                )}

                {/* Categoría: Visibilidad Móvil */}
                {activeSettingsTab === 'mobile' && (
                  <MobileSettingsTab 
                    initialMobileViews={mobileViewsLibre}
                    onSave={handleSaveMobileViews}
                    isDarkMode={isDarkMode}
                  />
                )}

                {/* Categoría: Automatización (AUTO) */}
                {activeSettingsTab === 'auto' && (
                  <motion.section 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
                  >
                    <div className="flex items-center gap-2 text-amber-500">
                      <Zap size={16} />
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Automatización (AUTO)</h3>
                    </div>
                    
                    <div className="space-y-6 pl-2">
                      {/* Atajos de Teclado */}
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Atajos de Teclado</label>
                        <div className="space-y-2">
                          <button onClick={() => setSpaceShortcutEnabled(!spaceShortcutEnabled)} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${spaceShortcutEnabled ? 'bg-blue-50 border-blue-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                            <div className="flex flex-col items-start">
                              <span className={`text-[10px] font-black uppercase ${spaceShortcutEnabled ? (isDarkMode ? 'text-blue-400' : 'text-blue-950') : 'text-gray-400'}`}>Espacio para Grabar</span>
                              <span className="text-[9px] text-gray-400 font-bold">Control rápido del Dictáfono</span>
                            </div>
                            {spaceShortcutEnabled ? <ToggleRight className="text-blue-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                          </button>

                          <button onClick={() => setAgentShortcutEnabled(!agentShortcutEnabled)} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${agentShortcutEnabled ? 'bg-indigo-50 border-indigo-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                            <div className="flex flex-col items-start">
                              <span className={`text-[10px] font-black uppercase ${agentShortcutEnabled ? (isDarkMode ? 'text-indigo-400' : 'text-blue-950') : 'text-gray-400'}`}>Doble Pulsación 'A'</span>
                              <span className="text-[9px] text-gray-400 font-bold">Llamada rápida al Agente IA</span>
                            </div>
                            {agentShortcutEnabled ? <ToggleRight className="text-indigo-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                          </button>
                        </div>
                      </div>

                      {/* Scripts de AutoHotkey */}
                      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                            Scripts AutoHotkey (Windows)
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[8px] font-black">PRO</span>
                          </label>
                          <a 
                            href="https://www.autohotkey.com/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[9px] font-black text-blue-500 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink size={10} />
                            Instalar AHK
                          </a>
                        </div>

                        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-amber-900/10 border-amber-900/30' : 'bg-amber-50 border-amber-100'}`}>
                          <p className="text-[10px] font-bold text-amber-800 dark:text-amber-200 leading-relaxed mb-4">
                            Descarga estos scripts para integrar MODULAR con tu PACS local. Requiere AutoHotkey instalado.
                          </p>
                          
                          <div className="space-y-2">
                            {[
                              { 
                                id: 'paste_enter', 
                                name: 'Pegado + Enter (Ctrl+Clic)', 
                                desc: 'Pega y salta línea automáticamente.',
                                code: '^LButton::\n    Send, ^v\n    Sleep, 50\n    Send, {Enter}\nreturn' 
                              },
                              { 
                                id: 'double_shift', 
                                name: 'Pegado Rápido (Doble Shift)', 
                                desc: 'Pega al pulsar Shift dos veces.',
                                code: '~Shift::\nif (A_PriorHotkey = "~Shift" && A_TimeSincePriorHotkey < 300)\n{\n    Send ^v\n}\nreturn' 
                              },
                              { 
                                id: 'shift_enter', 
                                name: 'Salto de Línea (Shift+Clic)', 
                                desc: 'Inserta un Enter con el ratón.',
                                code: '+LButton::\n    Send {Enter}\nreturn' 
                              },
                              { 
                                id: 'auto_paste_comm', 
                                name: 'Comunicación Auto-Pegado', 
                                desc: 'Pega automáticamente al hacer CLIC tras copiar.',
                                code: '#Persistent\n#SingleInstance Force\n\nGlobal PendientePegar := false\n\nOnClipboardChange:\n    if (A_EventInfo = 1) {\n        PendientePegar := true\n        ToolTip, MODULAR: Informe listo (Haz CLIC donde quieras pegarlo)\n        SetTimer, RemoveToolTip, -3000\n    }\nreturn\n\nRemoveToolTip:\n    ToolTip\nreturn\n\n~LButton::\n    if (PendientePegar) {\n        Sleep, 150\n        Send, ^v\n        PendientePegar := false\n        ToolTip, MODULAR: ¡Informe Pegado!\n        SetTimer, RemoveToolTip, -1000\n    }\nreturn' 
                              },
                              { 
                                id: 'send_to_phd', 
                                name: 'Enviar a PhD (Alt + Clic)', 
                                desc: 'Envía selección a PhD (Limpia y Genera).',
                                code: '!LButton:: ; Alt + Clic Izquierdo\n    Send, ^c ; Copia selección\n    ClipWait, 1\n    if ErrorLevel\n        return\n    \n    ; Activar ventana de MODULAR (Título oficial: RadioReport AI)\n    SetTitleMatchMode, 2\n    IfWinExist, RadioReport AI\n    {\n        WinActivate, RadioReport AI\n        WinWaitActive, RadioReport AI, , 2\n        if ErrorLevel\n            return\n            \n        ; Enviar comando secreto a MODULAR (Ctrl + Alt + P)\n        Sleep, 300\n        Send, ^!p\n    }\nreturn' 
                              }
                            ].map(script => (
                              <button 
                                key={script.id}
                                onClick={() => {
                                  const blob = new Blob([script.code], { type: 'text/plain' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${script.id}.ahk`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-amber-500/50' : 'bg-white border-gray-100 hover:border-amber-300 shadow-sm'}`}
                              >
                                <div className="flex flex-col items-start text-left">
                                  <span className="text-[10px] font-black uppercase tracking-tight">{script.name}</span>
                                  <span className="text-[9px] text-gray-400 font-bold">{script.desc}</span>
                                </div>
                                <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600">
                                  <UploadCloud size={16} className="rotate-180" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Portapapeles y PACS */}
                      <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                          Configuración de Portapapeles
                          <button onClick={() => toggleHelp('copy')} className="ml-auto text-blue-500 hover:text-blue-600"><HelpCircle size={12} /></button>
                        </label>
                        <div className="space-y-2">
                          <button onClick={() => setCopyRichTextEnabled(!copyRichTextEnabled)} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${copyRichTextEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                            <div className="flex flex-col items-start">
                              <span className={`text-[10px] font-black uppercase ${copyRichTextEnabled ? (isDarkMode ? 'text-emerald-400' : 'text-blue-950') : 'text-gray-400'}`}>Copiado Enriquecido</span>
                              <span className="text-[9px] text-gray-400 font-bold">Mantiene negritas y párrafos</span>
                            </div>
                            {copyRichTextEnabled ? <ToggleRight className="text-emerald-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                          </button>

                          <button onClick={() => setAutoPasteEnabled(!autoPasteEnabled)} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${autoPasteEnabled ? 'bg-blue-50 border-blue-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                            <div className="flex flex-col items-start">
                              <span className={`text-[10px] font-black uppercase ${autoPasteEnabled ? (isDarkMode ? 'text-blue-400' : 'text-blue-950') : 'text-gray-400'}`}>Pegado Automático</span>
                              <span className="text-[9px] text-gray-400 font-bold">Enviar al PACS tras copiar</span>
                            </div>
                            {autoPasteEnabled ? <ToggleRight className="text-blue-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                          </button>

                          {/* Plugin PACS Chrome Extension */}
                          <div className={`p-3 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <button
                                onClick={handleDownloadExtensionZip}
                                disabled={isGeneratingExtension}
                                className="flex-1 flex items-center justify-between text-left group disabled:opacity-50"
                              >
                                <div className="flex flex-col items-start text-left">
                                  <span className={`text-[10px] font-black uppercase tracking-tight ${isDarkMode ? 'text-blue-400' : 'text-blue-950'}`}>
                                    {isGeneratingExtension ? 'Generando...' : 'Descargar Plugin PACS'}
                                  </span>
                                  <span className="text-[9px] text-gray-400 font-bold">
                                    Extensión de Chrome para pegar el informe automáticamente en tu PACS
                                  </span>
                                </div>
                                <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform ml-2">
                                  {isGeneratingExtension ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                  ) : (
                                    <Download size={16} />
                                  )}
                                </div>
                              </button>
                              <button 
                                onClick={() => toggleHelp('pacsExtension')} 
                                className="p-2 text-blue-500 hover:text-blue-600 transition-colors"
                                title="Instrucciones de instalación"
                              >
                                <HelpCircle size={14} />
                              </button>
                            </div>

                            {visibleHelp.pacsExtension && (
                              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                                <div className="space-y-1.5 text-[10px] text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
                                  <div className="font-black text-[10px] uppercase text-blue-600 dark:text-blue-400 mb-1">
                                    Instrucciones de Instalación:
                                  </div>
                                  <ol className="list-decimal pl-4 space-y-1">
                                    <li>Descarga el archivo ZIP con el botón de arriba.</li>
                                    <li>Descomprime el ZIP en una carpeta de tu computadora (no la borres después, Chrome la necesita).</li>
                                    <li>Abre Chrome y ve a <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-[9px] text-blue-600 dark:text-blue-300 select-all">chrome://extensions</span></li>
                                    <li>Activa el "Modo de desarrollador" (interruptor arriba a la derecha).</li>
                                    <li>Haz clic en "Cargar extensión sin empaquetar" (o "Load unpacked").</li>
                                    <li>Selecciona la carpeta donde descomprimiste el ZIP.</li>
                                    <li>El icono de Modular aparecerá en la barra de extensiones de Chrome. Haz clic para abrir el panel lateral.</li>
                                  </ol>
                                </div>

                                <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-700 dark:text-amber-300 font-medium leading-snug">
                                  <span className="font-bold">Nota:</span> el acceso al micrófono dentro del panel lateral depende de los permisos ya concedidos a este sitio en Chrome. Si no funciona, visita la app en una pestaña normal primero y concede el permiso de micrófono antes de abrir el panel.
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.section>
                )}

                {/* Categoría: Sincronización y Memoria */}
                {activeSettingsTab === 'sync' && (
                  <section className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-2 text-emerald-500">
                      <Database size={16} />
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Sincronización y Memoria</h3>
                    </div>
                    
                    <div className="space-y-4 pl-2">
                      <button onClick={toggleTextMirroring} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${isTextMirroringActive ? 'bg-blue-50 border-blue-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                        <div className="flex flex-col items-start">
                          <span className={`text-[10px] font-black uppercase ${isTextMirroringActive ? (isDarkMode ? 'text-blue-400' : 'text-blue-700') : 'text-gray-400'}`}>Modo Espejo de Texto</span>
                          <span className="text-[9px] text-gray-400 font-bold">Recibir dictado desde el móvil</span>
                        </div>
                        {isTextMirroringActive ? <ToggleRight className="text-blue-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                      </button>

                      <button onClick={togglePersistence} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${windowPersistenceEnabled ? 'bg-blue-50 border-blue-200' : 'bg-transparent border-gray-100 dark:border-gray-800'}`}>
                        <div className="flex flex-col items-start">
                          <span className={`text-[10px] font-black uppercase ${windowPersistenceEnabled ? (isDarkMode ? 'text-blue-400' : 'text-slate-700') : 'text-gray-400'}`}>Persistencia Maestra</span>
                          <span className="text-[9px] text-gray-400 font-bold">Coordina ventanas y posiciones</span>
                        </div>
                        {windowPersistenceEnabled ? <ToggleRight className="text-blue-600" size={24} /> : <ToggleLeft className="text-gray-300" size={24} />}
                      </button>

                      <button 
                        onClick={() => performCloudBackup(true)} 
                        disabled={isBackingUpCloud}
                        className={`w-full p-4 rounded-xl border flex items-center justify-between transition-all ${backupSuccess ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100')}`}
                      >
                        <div className="flex flex-col items-start text-left">
                          <span className="text-[10px] font-black uppercase">Respaldo en la Nube</span>
                          <span className="text-[9px] font-bold opacity-60">Sincroniza con Google Cloud</span>
                        </div>
                        {isBackingUpCloud ? <RefreshCw size={18} className="animate-spin" /> : backupSuccess ? <CheckCircle size={18} /> : <UploadCloud size={18} />}
                      </button>

                      {/* Gestión de Ventanas Móviles */}
                      <div className="pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ventanas Registradas</label>
                          {Object.keys(staticPositions).length > 0 && (
                            <button 
                              onClick={clearStaticPositions}
                              className="text-[9px] font-black text-red-500 hover:text-red-600 uppercase tracking-wider flex items-center gap-1"
                            >
                              <RefreshCw size={10} />
                              Limpiar Todo
                            </button>
                          )}
                        </div>

                        {Object.keys(staticPositions).length === 0 ? (
                          <div className="p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-800 text-center">
                            <span className="text-[10px] font-bold text-gray-400 uppercase">No hay ventanas registradas</span>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                            {Object.entries(staticPositions).map(([id, pos]) => {
                              const meta = MODULE_META[id] || { label: id, icon: Smartphone };
                              return (
                                <div key={id} className={`flex items-center justify-between p-3 rounded-xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-100'}`}>
                                  <div className="flex items-center gap-3">
                                    <WindowPositionPreview pos={pos} isDarkMode={isDarkMode} />
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-black uppercase tracking-tight">{meta.label}</span>
                                      <span className="text-[8px] text-gray-400 font-bold uppercase">
                                        {pos.isPinned ? '📌 Anclada' : 'Temporal'} • {pos.w || pos.width}x{pos.h || pos.height}
                                      </span>
                                    </div>
                                  </div>
                                  <button 
                                    onClick={() => deleteStaticPosition(id)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                {/* Categoría: Herramientas de Sistema */}
                {activeSettingsTab === 'system' && (
                  <section className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Wrench size={16} />
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em]">Herramientas de Sistema</h3>
                    </div>
                    
                    <div className="space-y-3 pl-2">
                      <button 
                        onClick={handleHardReset}
                        className={`w-full py-3 rounded-xl border border-red-500/30 text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all`}
                      >
                        Reinicio Maestro del Sistema
                      </button>
                    </div>
                  </section>
                )}

              </div>

              {/* Footer */}
              <div className={`p-6 border-t text-center ${isDarkMode ? 'border-gray-800 bg-[#2a2d34]' : 'border-gray-100 bg-gray-50'}`}>
                <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.3em]">MODULAR App v4.0.3 by IMAGEN X.LLC</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      <TermsModal 
        isOpen={showTermsModal}
        isDarkMode={isDarkMode}
        onAccept={handleAcceptTerms}
      />

      <Modal isOpen={!!confirmModal} onClose={() => setConfirmModal(null)} title={confirmModal?.title || "CONFIRMACIÓN"}>
        <div className="p-4 space-y-6 text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${confirmModal?.type === 'danger' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
            {confirmModal?.type === 'danger' ? <ShieldAlert size={32} /> : <HelpCircle size={32} />}
          </div>
          <p className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{confirmModal?.message}</p>
          <div className="flex gap-3">
            <button onClick={() => setConfirmModal(null)} className={`flex-1 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest border ${isDarkMode ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>Cancelar</button>
            <button onClick={confirmModal?.onConfirm} className={`flex-1 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest text-white shadow-lg ${confirmModal?.type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>Confirmar</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!infoModal} onClose={() => setInfoModal(null)} title={infoModal?.title || "INFORMACIÓN"}>
        <div className="p-4 space-y-6 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <CheckCircle size={32} />
          </div>
          <p className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{infoModal?.message}</p>
          <button onClick={() => setInfoModal(null)} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:bg-emerald-700">Entendido</button>
        </div>
      </Modal>
    </div>
  );
};

export default ModularWorkspace;
